// Camplocation OSM import tool — React port of the standalone index.html.
// Pulls from OpenStreetMap via the Overpass API, maps results to the app's
// `places` schema, then lets the admin filter, bulk-edit and export JSON.
// Stores nothing. Rendered only for authenticated admins.
import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Pager, usePage } from './Pager';
import { useNavigate } from 'react-router-dom';
import { ref, get, set, update, query, orderByChild, startAt, endAt, limitToLast } from 'firebase/database';
import { ref as sRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useAuth } from './AuthContext';
import { auth, db, storage } from './firebase';
import {
  generateField,
  hasOpenAIKey,
  FIELD_PROMPTS,
  PROMPT_FIELDS,
  PLACETYPE_ENUM,
  wantsContactInfo,
  AI_MODELS,
  DEFAULT_AI_MODEL,
  setAIModel,
  estimateCostPerLocation,
  getAIUsage,
  resetAIUsage,
} from './openai';
import UsersView from './UsersView';
import type { OverturePlace } from './overture';
import './AdminPanel.css';

/* ============================================================
   Pure helpers / constants (ported verbatim from index.html)
   ============================================================ */

// Public Overpass servers that answer browser requests (CORS), tried in order
// until one gives a real answer. Checked 2026-10-21; overpass.osm.jp is left
// out because it sends no CORS header. kumi often hangs, so it goes last.
const OVERPASS = [
  'https://overpass-api.de/api/interpreter',
  'https://overpass.private.coffee/api/interpreter',
  'https://maps.mail.ru/osm/tools/overpass/api/interpreter',
  'https://overpass.kumi.systems/api/interpreter',
];
// The last server that worked goes first next time (until it fails).
let overpassFirst = 0;
const overpassHost = (u: string) => new URL(u).hostname;

// Runs one Overpass query, moving to the next server when one is busy
// (429 / 5xx), times out, is unreachable or returns no usable JSON. A 400 is
// the query itself being wrong, so no other server is tried then.
async function overpassFetch(
  q: string,
  hardMs: number,
  onTry: (host: string, n: number, of: number) => void
): Promise<{ data: any; err: string | null; host: string }> {
  const order = OVERPASS.map((_, i) => OVERPASS[(overpassFirst + i) % OVERPASS.length]);
  const errs: string[] = [];
  for (let i = 0; i < order.length; i++) {
    const url = order[i];
    const host = overpassHost(url);
    onTry(host, i + 1, order.length);
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => ctrl.abort(), hardMs);
    try {
      const r = await fetch(url, {
        method: 'POST',
        body: 'data=' + encodeURIComponent(q),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        signal: ctrl.signal,
      });
      if (r.status === 400) return { data: null, err: `the query was rejected by ${host} (HTTP 400)`, host };
      if (!r.ok) {
        errs.push(`${host}: ${r.status === 429 || r.status >= 500 ? 'busy' : 'error'} (HTTP ${r.status})`);
        continue;
      }
      const data = await r.json().catch(() => null);
      if (!data || !Array.isArray(data.elements)) {
        errs.push(`${host}: no usable answer`);
        continue;
      }
      // Overpass reports its own timeouts / memory limits in a 200 "remark"
      // with partial results — another server may manage the whole query.
      if (typeof data.remark === 'string' && /runtime error/i.test(data.remark)) {
        errs.push(`${host}: ${data.remark.slice(0, 80)}`);
        continue;
      }
      overpassFirst = OVERPASS.indexOf(url);
      return { data, err: null, host };
    } catch (e: any) {
      errs.push(`${host}: ${e?.name === 'AbortError' ? `timed out after ${Math.round(hardMs / 1000)} s` : 'unreachable'}`);
    } finally {
      window.clearTimeout(timer);
    }
  }
  return { data: null, err: `every OpenStreetMap server failed — ${errs.join('; ')}`, host: '' };
}

interface Category {
  id: string;
  label: string;
  on: boolean;
  sel: string[]; // Overpass selectors used to fetch this category
  ovt: string[]; // Overture taxonomy.primary values for this category ([] = none)
  placeType: string; // app placeType assigned to matches
  match: (t: OsmTags) => boolean; // reverse map: does an OSM element belong here?
}

// One entry per app "By Places" type that has an OpenStreetMap equivalent.
// `sel` drives the Overpass query; `match` reverse-maps a fetched element to
// this type. Order matters: more specific categories must come first so
// placeTypeFor() returns the best match.
const mh = (t: OsmTags) =>
  t.amenity === 'parking' && (t.motorhome === 'designated' || t.motorhome === 'yes' || t.caravan === 'designated');
const CATEGORIES: Category[] = [
  { id: 'camp_site', label: 'Campsite', on: false, sel: ['["tourism"="camp_site"]'], ovt: ['campground'], placeType: 'Campsite',
    match: (t) => t.tourism === 'camp_site' },
  { id: 'caravan_site', label: 'Caravan park', on: false, sel: ['["tourism"="caravan_site"]'], ovt: ['rv_park'], placeType: 'Caravan park',
    match: (t) => t.tourism === 'caravan_site' },
  { id: 'motorhome_free', label: 'Free motorhome place', on: false,
    sel: ['["amenity"="parking"]["motorhome"="designated"]["fee"="no"]', '["amenity"="parking"]["motorhome"="yes"]["fee"="no"]'],
    ovt: [], placeType: 'Free motorhome place', match: (t) => mh(t) && (t.fee === 'no' || t.fee === 'free') },
  { id: 'motorhome_paid', label: 'Paid motorhome place', on: false,
    sel: ['["amenity"="parking"]["motorhome"="designated"]["fee"="yes"]', '["amenity"="parking"]["motorhome"="yes"]["fee"="yes"]'],
    ovt: [], placeType: 'Paid motorhome place', match: (t) => mh(t) && (t.fee === 'yes' || !!t.charge) },
  { id: 'motorhome', label: 'Motorhome place (any)', on: false,
    sel: ['["amenity"="parking"]["motorhome"="designated"]', '["amenity"="parking"]["motorhome"="yes"]'],
    ovt: ['rv_park'], placeType: 'Free motorhome place', match: (t) => mh(t) },
  { id: 'private_parking', label: 'Private parking lot', on: false,
    sel: ['["amenity"="parking"]["access"="private"]', '["amenity"="parking"]["access"="customers"]'],
    ovt: [], placeType: 'Private parking lot', match: (t) => t.amenity === 'parking' && (t.access === 'private' || t.access === 'customers') },
  { id: 'parking', label: 'Parking lot', on: false, sel: ['["amenity"="parking"]'], ovt: ['parking', 'parking_lot'], placeType: 'Parking lot',
    match: (t) => t.amenity === 'parking' },
  { id: 'motorway', label: 'Motorway services', on: false, sel: ['["highway"="services"]'], ovt: ['truck_stop'], placeType: 'Motorway',
    match: (t) => t.highway === 'services' },
  { id: 'rest_area', label: 'Rest area / picnic', on: false, sel: ['["highway"="rest_area"]', '["tourism"="picnic_site"]'], ovt: ['rest_stop'],
    placeType: 'Rest area', match: (t) => t.highway === 'rest_area' || t.tourism === 'picnic_site' },
  { id: 'nature', label: 'Place in nature', on: false,
    sel: ['["tourism"="viewpoint"]', '["leisure"="nature_reserve"]', '["amenity"="drinking_water"]'],
    ovt: ['scenic_viewpoint', 'lookout', 'nature_reserve'], placeType: 'Place in nature', match: (t) => t.tourism === 'viewpoint' || t.leisure === 'nature_reserve' || t.amenity === 'drinking_water' },
  { id: 'offroad', label: 'Off road 4x4', on: false, sel: ['["highway"="track"]["4wd_only"="yes"]'], ovt: [], placeType: 'Off road 4x4',
    match: (t) => t.highway === 'track' && t['4wd_only'] === 'yes' },
];

/* ---------- Overture layer ----------
   'both' = OSM first, then Overture as a check: only OSM places that Overture
   also has are kept (their missing contact details filled from it); places
   only Overture has are not added. */
type DataSource = 'both' | 'osm' | 'overture';
// An Overture place within OVT_MATCH_M of an OSM result with a similar name,
// or within OVT_SAME_SPOT_M whatever the names, is that same place.
const OVT_MATCH_M = 60;
const OVT_SAME_SPOT_M = 20;
// OSM tags equivalent to each Overture category, so Overture places run
// through the same mapping (mapElement, placeTypeFor, derive*) as OSM ones.
const OVT_TAGS: Record<string, Record<string, string>> = {
  campground: { tourism: 'camp_site' },
  rv_park: { tourism: 'caravan_site' },
  parking: { amenity: 'parking' },
  parking_lot: { amenity: 'parking' },
  truck_stop: { highway: 'services' },
  rest_stop: { highway: 'rest_area' },
  scenic_viewpoint: { tourism: 'viewpoint' },
  lookout: { tourism: 'viewpoint' },
  nature_reserve: { leisure: 'nature_reserve' },
};
function overtureElement(p: OverturePlace) {
  const tags: Record<string, string> = { ...OVT_TAGS[p.category], 'overture:id': p.id };
  if (p.name) tags.name = p.name;
  if (p.phone) tags.phone = p.phone;
  if (p.website) tags.website = p.website;
  if (p.email) tags.email = p.email;
  if (p.address) tags['addr:full'] = p.address;
  return { type: 'overture', id: p.id, lat: p.lat, lon: p.lon, tags };
}

interface OsmTags {
  [k: string]: any;
}
interface ImageEntry {
  dateUploaded: string;
  uploadedBy: string;
  url: string;
}
interface PlaceItem {
  _id: string;
  _sel: boolean;
  _uploaded?: boolean; // set once pushed to Firebase, so re-uploads skip it
  _dupWarning?: string; // set when upload was skipped as a likely duplicate
  _photoWarning?: string; // set by "Check" when a photo is missing or corrupt
  _country?: string; // ISO code of the country the fetch was limited to
  _src?: 'osm' | 'overture' | 'osm+overture'; // which data layer(s) the row came from
  _osm: { type: string; id: number; tags: OsmTags };
  activities: string[];
  addedBy: string;
  dateAdded: string;
  description: string;
  email: string;
  heightLimit: string;
  images: Record<string, ImageEntry>;
  lastUpdatedCostsDate: string;
  latitude: number;
  longitude: number;
  paymentStatus: string;
  phone: string;
  placeType: string;
  rating: number;
  services: string[];
  website: string;
  [k: string]: any;
}

// The account name new locations are attributed to. Editable in the UI
// ("Import defaults" → addedBy), this is just the default.
const DEFAULT_UPLOADER = 'Karve';

// "DD/MM/YYYY HH:mm:ss" — the format the live database uses for dateAdded,
// images.*.dateUploaded, and lastUpdatedCostsDate.
function fmtDateSlash(d: Date) {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

// Fetch an external image through our server (avoids CORS) so it can be
// re-uploaded into Firebase Storage.
async function fetchImageBlob(url: string): Promise<Blob> {
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error('Not signed in');
  const r = await fetch('/api/fetch-image?url=' + encodeURIComponent(url), {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!r.ok) {
    const t = await r.text().catch(() => '');
    throw new Error(`image fetch ${r.status}: ${t.slice(0, 120)}`);
  }
  const b = await r.blob();
  if (!b.size) throw new Error('empty image response');
  return b;
}

// Satellite (Esri export) and other external photo sources occasionally
// return a transient error (e.g. Esri's HTTP 500 "Error: bytes", a network
// blip) that succeeds on a second try. Retry a few times with backoff before
// giving up, so a single flaky response doesn't silently drop the photo.
async function fetchImageBlobWithRetry(url: string, attempts = 3): Promise<Blob> {
  let lastErr: any;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fetchImageBlob(url);
    } catch (e) {
      lastErr = e;
      if (i < attempts - 1) await new Promise((r) => setTimeout(r, 400 * (i + 1)));
    }
  }
  throw lastErr;
}

// GeoFire-compatible geohash (base32, precision 10). Every places/{id} entry
// has a places_locations/{id} = { g, l:[lat,lng] } twin that the mobile app's
// map queries — uploads and coordinate edits must keep it in sync.
const GEOHASH_CHARS = '0123456789bcdefghjkmnpqrstuvwxyz';
function geohash(lat: number, lon: number, precision = 10): string {
  let latMin = -90, latMax = 90, lonMin = -180, lonMax = 180;
  let hash = '';
  let bits = 0;
  let bitCount = 0;
  let even = true;
  while (hash.length < precision) {
    if (even) {
      const mid = (lonMin + lonMax) / 2;
      if (lon > mid) { bits = bits * 2 + 1; lonMin = mid; } else { bits = bits * 2; lonMax = mid; }
    } else {
      const mid = (latMin + latMax) / 2;
      if (lat > mid) { bits = bits * 2 + 1; latMin = mid; } else { bits = bits * 2; latMax = mid; }
    }
    even = !even;
    if (++bitCount === 5) {
      hash += GEOHASH_CHARS.charAt(bits);
      bits = 0;
      bitCount = 0;
    }
  }
  return hash;
}

// Great-circle distance in metres between two coordinates (Haversine).
function haversineMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
// Two locations this close together are treated as the same place.
const DUPLICATE_RADIUS_M = 75;
// Nearest distance (metres) from (lat, lon) to any point in `points`, or
// Infinity if `points` is empty. Plain loop (not Math.min(...spread)) so it
// doesn't blow the call stack against a large existing-locations set.
function nearestDistanceMeters(lat: number, lon: number, points: { lat: number; lon: number }[]): number {
  let best = Infinity;
  for (const p of points) {
    const d = haversineMeters(lat, lon, p.lat, p.lon);
    if (d < best) best = d;
  }
  return best;
}
// Existing locations (all users) near the given rows, from places_locations —
// the lightweight {g: geohash, l: [lat, lon]} index kept in sync with places/.
// Only the ~5 km geohash cells the rows' duplicate radius touches are read,
// one prefix query each, so the cost follows the search area, not the size of
// the database. Needs ".indexOn": ["g"] on places_locations (GeoFire uses the
// same); without it, falls back to reading the whole index (`fullScan`).
const GEO_CELL_PRECISION = 5;
type GeoPoint = { lat: number; lon: number };
function pointsOf(val: Record<string, any>): GeoPoint[] {
  return Object.values<any>(val || {})
    .map((v) => (Array.isArray(v?.l) ? { lat: v.l[0], lon: v.l[1] } : null))
    .filter((p): p is GeoPoint => !!p && Number.isFinite(p.lat) && Number.isFinite(p.lon));
}
async function loadExistingPointsNear(
  rows: { latitude: number; longitude: number }[]
): Promise<{ points: GeoPoint[]; fullScan: boolean }> {
  const cells = new Set<string>();
  for (const r of rows) {
    // The cells under the corners of the duplicate box around the point; a
    // 5 km cell is far bigger than the box, so the corners cover all of it.
    const dLat = DUPLICATE_RADIUS_M / 111320;
    const dLon = DUPLICATE_RADIUS_M / (111320 * Math.max(0.01, Math.cos((r.latitude * Math.PI) / 180)));
    for (const la of [r.latitude - dLat, r.latitude + dLat])
      for (const lo of [r.longitude - dLon, r.longitude + dLon]) cells.add(geohash(la, lo, GEO_CELL_PRECISION));
  }
  const points: GeoPoint[] = [];
  try {
    await pool(Array.from(cells), 8, async (cell) => {
      const snap = await get(
        query(ref(db, 'places_locations'), orderByChild('g'), startAt(cell), endAt(cell + ''))
      );
      points.push(...pointsOf(snap.val()));
    });
    return { points, fullScan: false };
  } catch (err: any) {
    if (!/index/i.test(String(err?.message))) throw err;
    const snap = await get(ref(db, 'places_locations'));
    return { points: pointsOf(snap.val()), fullScan: true };
  }
}
const NO_GEO_INDEX_NOTE =
  ' ⚠ Slow: places_locations has no index on "g", so the WHOLE geo index was downloaded — add ".indexOn": ["g"] under "places_locations" in the Realtime Database rules.';

/* ---------- photo sanity check ----------
   A 200 response is no proof of a photo: the proxy can hand back an HTML
   error page, map services answer with 1×1 placeholders, and a truncated
   JPEG downloads happily but never paints. So every photo is sniffed for a
   real image header and then actually decoded before it is allowed into
   Storage. Whatever fails is dropped and reported; the location itself still
   uploads, just without that photo. */
const MIN_IMAGE_BYTES = 1024; // below this it is an error page or a stub
const MIN_IMAGE_SIDE = 48; // 1×1 trackers and "no imagery" placeholders

async function imageSize(blob: Blob): Promise<{ width: number; height: number }> {
  if (typeof createImageBitmap === 'function') {
    const bmp = await createImageBitmap(blob);
    const size = { width: bmp.width, height: bmp.height };
    bmp.close?.();
    return size;
  }
  const url = URL.createObjectURL(blob);
  try {
    return await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve({ width: i.naturalWidth, height: i.naturalHeight });
      i.onerror = () => reject(new Error('decode failed'));
      i.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

// '' when the blob is a displayable image, otherwise the reason it is not.
async function imageProblem(blob: Blob): Promise<string> {
  if (!blob.size) return 'empty file';
  if (blob.size < MIN_IMAGE_BYTES) return `only ${blob.size} bytes — not a real photo`;
  const head = new Uint8Array(await blob.slice(0, 16).arrayBuffer());
  const magic = (...bytes: number[]) => bytes.every((b, i) => head[i] === b);
  const known =
    magic(0xff, 0xd8, 0xff) || // JPEG
    magic(0x89, 0x50, 0x4e, 0x47) || // PNG
    magic(0x47, 0x49, 0x46, 0x38) || // GIF87a/89a
    (magic(0x52, 0x49, 0x46, 0x46) && String.fromCharCode(...Array.from(head.slice(8, 12))) === 'WEBP');
  if (!known) {
    const text = new TextDecoder().decode(head).trim();
    return /^[<{]/.test(text) ? 'server returned a page, not an image' : 'not a JPEG/PNG/GIF/WebP file';
  }
  // The header can be intact while the rest is truncated, so decode it too.
  try {
    const { width, height } = await imageSize(blob);
    if (!width || !height) return 'image could not be decoded';
    if (width < MIN_IMAGE_SIDE || height < MIN_IMAGE_SIDE) return `too small (${width}×${height})`;
  } catch (e: any) {
    return `corrupt image (${e?.message || e})`;
  }
  return '';
}

// Downscale a photo before uploading (phones produce 5–15 MB originals).
// Longest side capped at `maxDim`, re-encoded as JPEG. Falls back to the
// original file if decoding fails (e.g. unsupported format).
async function downscaleImage(file: File, maxDim = 1600, quality = 0.85): Promise<Blob> {
  try {
    const url = URL.createObjectURL(file);
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    URL.revokeObjectURL(url);
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (blob) return blob;
  } catch {
    /* fall through to the original file */
  }
  return file;
}

// run async fn over items with limited concurrency
async function pool<T>(items: T[], n: number, fn: (item: T) => Promise<void>) {
  const q = [...items];
  await Promise.all(
    Array.from({ length: n }, async () => {
      while (q.length) await fn(q.shift() as T);
    })
  );
}

function osmImageUrls(t: OsmTags): string[] {
  const urls: string[] = [];
  if (t.image && /^https?:\/\//.test(t.image)) urls.push(t.image);
  if (t.wikimedia_commons) {
    const f = String(t.wikimedia_commons).replace(/^File:/i, '');
    urls.push('https://commons.wikimedia.org/wiki/Special:FilePath/' + encodeURIComponent(f) + '?width=640');
  }
  return urls;
}
function svUrl(lat: number, lon: number, key: string) {
  return `https://maps.googleapis.com/maps/api/streetview?size=640x400&location=${lat},${lon}&fov=80&key=${key}`;
}
// Free, keyless satellite image from Esri World Imagery (top-down aerial view).
// Builds a square bbox `meters` across around the point and exports a JPEG.
function satUrl(lat: number, lon: number, meters = 200) {
  const half = meters / 2;
  const dLat = half / 111320; // metres per degree latitude
  const dLon = half / (111320 * Math.cos((lat * Math.PI) / 180));
  const west = (lon - dLon).toFixed(6);
  const east = (lon + dLon).toFixed(6);
  const south = (lat - dLat).toFixed(6);
  const north = (lat + dLat).toFixed(6);
  // Scale the pixel size to the ground area: Esri's export returns HTTP 500
  // ("Error: bytes") when asked for finer resolution than the imagery holds
  // (a small bbox with a large fixed size). ~2 px/metre, clamped to 128–640 px,
  // keeps every request comfortably above that limit.
  const size = Math.min(640, Math.max(128, Math.round(meters * 2)));
  return (
    'https://server.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer/export' +
    `?bbox=${west},${south},${east},${north}&bboxSR=4326&imageSR=4326&size=${size},${size}&format=jpg&f=image`
  );
}
async function svExists(lat: number, lon: number, key: string) {
  try {
    const r = await fetch(`https://maps.googleapis.com/maps/api/streetview/metadata?location=${lat},${lon}&key=${key}`);
    const j = await r.json();
    return j.status === 'OK';
  } catch {
    return true; // CORS-blocked? assume yes, img tag still works
  }
}
function imagesMap(urls: string[], uploadedBy: string): Record<string, ImageEntry> {
  const o: Record<string, ImageEntry> = {};
  const when = fmtDateSlash(new Date());
  urls.forEach((u, i) => {
    o['image_' + i] = { dateUploaded: when, uploadedBy: uploadedBy || DEFAULT_UPLOADER, url: u };
  });
  return o;
}
// Photo order is the N in image_N. Firebase hands keys back sorted as text
// (image_10 before image_2), so never rely on object order — sort by N.
function imageKeyNum(k: string) {
  const m = k.match(/^image_(\d+)$/);
  return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
}
function orderedImages(images: Record<string, ImageEntry> | undefined): [string, ImageEntry][] {
  return Object.entries(images || {}).sort(([a], [b]) => imageKeyNum(a) - imageKeyNum(b) || a.localeCompare(b));
}
// Re-key photos as image_0..image_N-1 in the given key order.
function renumberImages(images: Record<string, ImageEntry>, keys: string[]): Record<string, ImageEntry> {
  const o: Record<string, ImageEntry> = {};
  keys.forEach((k, i) => {
    if (images[k]) o['image_' + i] = images[k];
  });
  return o;
}
// Next free image_N for a place. Reordering renumbers the DB keys but not the
// Storage files, so the file names in the URLs count too — otherwise a new
// upload could overwrite a file another photo still points at.
function nextImageNum(images: Record<string, ImageEntry> | undefined) {
  let max = -1;
  for (const [k, v] of Object.entries(images || {})) {
    const n = imageKeyNum(k);
    if (n !== Number.MAX_SAFE_INTEGER) max = Math.max(max, n);
    let u = v?.url || '';
    try {
      u = decodeURIComponent(u);
    } catch {
      /* keep raw */
    }
    for (const m of Array.from(u.matchAll(/image_(\d+)(?:_\d+)?\.jpg/g))) max = Math.max(max, Number(m[1]));
  }
  return max + 1;
}
function firstImageUrl(l: PlaceItem) {
  const e = orderedImages(l.images);
  return e.length ? e[0][1].url : null;
}
function imageCount(l: PlaceItem) {
  return Object.keys(l.images || {}).length;
}

function parseList(s: string) {
  return (s || '').split(',').map((x) => x.trim()).filter(Boolean);
}
function mergeList(a: string[], b: string[]) {
  return Array.from(new Set([...(a || []), ...(b || [])]));
}

// Firebase push-id generator (standard algorithm)
const PUSH_CHARS = '-0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz';
let lastPushTime = 0;
const lastRand: number[] = [];
function pushId() {
  let now = Date.now();
  const dup = now === lastPushTime;
  lastPushTime = now;
  const ts = new Array(8);
  for (let i = 7; i >= 0; i--) {
    ts[i] = PUSH_CHARS.charAt(now % 64);
    now = Math.floor(now / 64);
  }
  let id = ts.join('');
  if (!dup) {
    for (let i = 0; i < 12; i++) lastRand[i] = Math.floor(Math.random() * 64);
  } else {
    let i = 11;
    for (; i >= 0 && lastRand[i] === 63; i--) lastRand[i] = 0;
    lastRand[i]++;
  }
  for (let i = 0; i < 12; i++) id += PUSH_CHARS.charAt(lastRand[i]);
  return id;
}

const yes = (v: any) => v && v !== 'no' && v !== '0' && v !== 'false';

// A whole-element "key=value" text blob (lowercased). Facets match against this
// so a service/activity is detected no matter WHICH OSM key holds it — the same
// fact often lives under different tags across contributors (e.g. a shower may be
// shower=yes, service:shower=yes, or amenity=shower). This is what lets us
// "search within all of the fields" instead of one hard-coded key.
function tagText(t: OsmTags): string {
  return Object.entries(t)
    .map(([k, v]) => `${k}=${v}`)
    .join('\n')
    .toLowerCase();
}

// A filterable facet shared by three things: the "By services"/"By activities"
// checkboxes, the OSM→place mapping (deriveServices/deriveActivities), and the
// post-search narrowing. `label` MUST match the app's enum spelling exactly
// (see openai.ts / the Filters screen). `test` receives the raw tags AND the
// full lowercased blob so it can look everywhere, not at a single key.
interface Facet {
  id: string;
  label: string;
  sel: string[]; // Overpass tag selectors used to FETCH this facet when no place category is picked
  test: (t: OsmTags, blob: string) => boolean;
}

// Maps OSM tags to the app's exact service enum labels (see Filters screen).
// `sel` drives the Overpass fetch when a service is chosen on its own; `test`
// (which searches every field) governs detection and post-fetch narrowing.
const SERVICE_FACETS: Facet[] = [
  { id: 'toilets', label: 'Toilets', sel: ['["toilets"="yes"]', '["amenity"="toilets"]'],
    test: (t, b) => yes(t.toilets) || t.amenity === 'toilets' || /toilet/.test(b) },
  { id: 'water', label: 'Water',
    sel: ['["drinking_water"="yes"]', '["amenity"="drinking_water"]', '["amenity"="water_point"]', '["man_made"="water_tap"]'],
    test: (t, b) => yes(t.drinking_water) || t.amenity === 'drinking_water' || yes(t.water_point) ||
      t.amenity === 'water_point' || /drinking_water|water_point|water_tap|water_supply|fresh_water/.test(b) },
  { id: 'shower', label: 'Shower', sel: ['["shower"="yes"]', '["amenity"="shower"]'],
    test: (t, b) => yes(t.shower) || yes(t.showers) || /shower/.test(b) },
  { id: 'kitchen', label: 'Kitchen/Dish wash', sel: ['["kitchen"="yes"]', '["amenity"="kitchen"]', '["dishwashing"="yes"]'],
    test: (t, b) => yes(t.kitchen) || yes(t.dishwashing) || yes(t.dishwasher) || t.amenity === 'kitchen' ||
      /kitchen|dish[_ ]?wash|dishwasher/.test(b) },
  { id: 'laundry', label: 'Laundrette', sel: ['["laundry"="yes"]', '["amenity"="laundry"]', '["washing_machine"="yes"]'],
    test: (t, b) => yes(t.laundry) || t.amenity === 'laundry' || yes(t.washing_machine) || /laundr|washing_machine/.test(b) },
  { id: 'internet_4g', label: '4G internet', sel: ['["internet_access"="cellular"]'],
    test: (t, b) => t.internet_access === 'cellular' || t['internet_access:type'] === 'cellular' || yes(t['4g']) ||
      /cellular|mobile_network|(^|[^a-z0-9])[45]g([^a-z0-9]|$)/.test(b) },
  { id: 'wifi', label: 'Access to Wifi', sel: ['["internet_access"="wlan"]', '["wifi"="yes"]', '["internet_access"="wifi"]'],
    test: (t, b) => t.internet_access === 'wlan' || yes(t.wifi) || yes(t.wlan) ||
      (yes(t.internet_access) && t.internet_access !== 'cellular') || /wifi|wlan/.test(b) },
  { id: 'rubbish', label: 'Rubbish bins', sel: ['["amenity"="waste_basket"]', '["waste_basket"="yes"]'],
    test: (t, b) => t.amenity === 'waste_basket' || yes(t.waste_basket) || yes(t.rubbish_bin) || yes(t.bin) ||
      /waste_basket|rubbish|litter|(^|\n)bin=/.test(b) },
  { id: 'picnic', label: 'Picnic table', sel: ['["leisure"="picnic_table"]', '["picnic_table"="yes"]', '["tourism"="picnic_site"]'],
    test: (t, b) => yes(t.picnic_table) || t.leisure === 'picnic_table' || t.tourism === 'picnic_site' || /picnic/.test(b) },
  { id: 'bbq', label: 'BBQ/Campfire', sel: ['["amenity"="bbq"]', '["bbq"="yes"]', '["leisure"="firepit"]', '["fireplace"="yes"]'],
    test: (t, b) => yes(t.bbq) || yes(t.barbecue_grill) || t.amenity === 'bbq' || yes(t.fireplace) || t.leisure === 'firepit' ||
      /bbq|barbecue|firepit|fireplace|campfire/.test(b) },
  { id: 'greywater', label: 'Wastewater/Grey water',
    sel: ['["amenity"="sanitary_dump_station"]', '["sanitary_dump_station"="yes"]', '["waste_disposal"]'],
    test: (t, b) => yes(t.sanitary_dump_station) || t.amenity === 'sanitary_dump_station' || yes(t.greywater) ||
      yes(t.waste_disposal) || /sanitary_dump|grey[_ ]?water|waste[_ ]?water|waste_disposal|dump_station/.test(b) },
  { id: 'blackwater', label: 'Black water', sel: ['["sanitary_dump_station:chemical_toilet"="yes"]', '["toilet_disposal"="yes"]'],
    test: (t, b) => yes(t.blackwater) || t['sanitary_dump_station:chemical_toilet'] === 'yes' || yes(t.toilet_disposal) ||
      /black[_ ]?water|chemical_toilet|toilet_disposal|cassette/.test(b) },
  { id: 'electricity', label: 'Electricity', sel: ['["power_supply"="yes"]', '["amenity"="power_supply"]', '["electricity"="yes"]'],
    test: (t, b) => yes(t.power_supply) || yes(t.electricity) || !!t['socket:type2'] ||
      /electricity|(^|\n)socket|power_supply/.test(b) },
  { id: 'ev_charger', label: 'Electric car charger', sel: ['["amenity"="charging_station"]'],
    test: (t, b) => t.amenity === 'charging_station' || /charging_station|car_charger|ev_charg/.test(b) },
  { id: 'ehu', label: 'Electric hook-up', sel: ['["power_supply"="ehu"]', '["electric_hookup"="yes"]'],
    test: (t, b) => yes(t.electric_hookup) || t.power_supply === 'ehu' || yes(t['power_supply:ehu']) ||
      /ehu|hook[_ -]?up|electric_hookup/.test(b) },
  { id: 'cafe', label: 'Cafe/Restaurant', sel: ['["amenity"="cafe"]', '["amenity"="restaurant"]', '["amenity"="fast_food"]'],
    test: (t, b) => t.amenity === 'cafe' || t.amenity === 'restaurant' || t.amenity === 'fast_food' ||
      /restaurant|(^|\n|=)cafe|fast_food/.test(b) },
  { id: 'shop', label: 'Shop/Groceries', sel: ['["shop"="convenience"]', '["shop"="supermarket"]', '["shop"="grocery"]'],
    test: (t, b) => t.shop === 'convenience' || t.shop === 'supermarket' || t.shop === 'grocery' || yes(t.shop) ||
      /supermarket|convenience|grocery|(^|\n)shop=/.test(b) },
  { id: 'bottled_gas', label: 'Bottled gas', sel: ['["shop"="gas"]', '["bottled_gas"="yes"]'],
    test: (t, b) => yes(t.bottled_gas) || t.shop === 'gas' || /bottled_gas|gas_bottle|shop=gas/.test(b) },
  { id: 'lpg', label: 'LPG', sel: ['["fuel:lpg"="yes"]', '["lpg"="yes"]'],
    test: (t, b) => t['fuel:lpg'] === 'yes' || yes(t.lpg) || /lpg/.test(b) },
  { id: 'pets', label: 'Pets allowed', sel: ['["dog"="yes"]', '["dogs"="yes"]'],
    test: (t, b) => t.dog === 'yes' || t.dogs === 'yes' || /dog=yes|dogs=yes|pets?=yes|dog=leashed/.test(b) },
  { id: 'vehicle_wash', label: 'Vehicle wash', sel: ['["amenity"="car_wash"]', '["car_wash"="yes"]'],
    test: (t, b) => t.amenity === 'car_wash' || yes(t.car_wash) || yes(t.vehicle_wash) || /car_wash|vehicle_wash/.test(b) },
];

// Maps OSM tags to the app's exact activity enum labels (see Filters screen).
const ACTIVITY_FACETS: Facet[] = [
  { id: 'hikes', label: 'Start of hikes', sel: ['["route"="hiking"]', '["network"="hiking"]', '["hiking"="yes"]', '["highway"="trailhead"]'],
    test: (t, b) => yes(t.hiking) || t.route === 'hiking' || t.network === 'hiking' || /hiking|trailhead|route=foot/.test(b) },
  { id: 'swimming', label: 'Swimming available',
    sel: ['["leisure"="swimming_area"]', '["sport"="swimming"]', '["natural"="beach"]', '["leisure"="beach_resort"]'],
    test: (t, b) => yes(t.swimming) || t.leisure === 'swimming_area' || t.sport === 'swimming' || t.natural === 'beach' ||
      /swimming|beach|bathing/.test(b) },
  { id: 'fishing', label: 'Fishing available', sel: ['["leisure"="fishing"]', '["sport"="fishing"]', '["fishing"="yes"]'],
    test: (t, b) => yes(t.fishing) || t.sport === 'fishing' || t.leisure === 'fishing' || /fishing|angling/.test(b) },
  { id: 'kayak', label: 'Kayak/boat release place', sel: ['["leisure"="slipway"]', '["waterway"="slipway"]', '["canoe"="yes"]'],
    test: (t, b) => t.leisure === 'slipway' || yes(t.canoe) || yes(t.kayak) || t.sport === 'canoe' || t.waterway === 'slipway' ||
      /slipway|kayak|canoe|boat_ramp|boat_launch/.test(b) },
  { id: 'mtb', label: 'Mountain bike tracks', sel: ['["route"="mtb"]', '["mtb"="yes"]', '["mtb:scale"]'],
    test: (t, b) => t.mtb === 'yes' || yes(t['mtb:scale']) || t.route === 'mtb' || t.sport === 'mtb' || /mtb|mountain_bik/.test(b) },
  { id: 'citybike', label: 'City bike routes', sel: ['["route"="bicycle"]', '["amenity"="bicycle_rental"]'],
    test: (t, b) => t.bicycle === 'yes' || yes(t.cycling) || t.route === 'bicycle' || /cycl|bicycle|(^|\n)bike/.test(b) },
  { id: 'playground', label: 'Children playground', sel: ['["leisure"="playground"]'],
    test: (t, b) => t.leisure === 'playground' || yes(t.playground) || /playground/.test(b) },
  { id: 'viewpoint', label: 'Point of view', sel: ['["tourism"="viewpoint"]'],
    test: (t, b) => t.tourism === 'viewpoint' || /viewpoint/.test(b) },
  { id: 'touristic', label: 'Near touristic places', sel: ['["tourism"="attraction"]', '["tourism"="museum"]', '["historic"]'],
    test: (t, b) => t.tourism === 'attraction' || t.tourism === 'museum' || !!t.historic ||
      /attraction|museum|historic|monument|(^|\n)castle|ruins/.test(b) },
];

// The exact values the mobile app's Filters screen offers — what a services /
// activities checkbox list is built from. Free text here means a place quietly
// drops out of those filters, which is why the cells no longer accept any.
const SERVICE_OPTIONS = SERVICE_FACETS.map((f) => f.label);
const ACTIVITY_OPTIONS = ACTIVITY_FACETS.map((f) => f.label);

/* ---------- countries the import can be limited to ----------
   The code is OSM's ISO3166-1 on the country's admin_level=2 area, which is
   what lets the Overpass query drop everything outside the country server-side
   — a map view near a border otherwise drags in the neighbours' places. */
// `dial` is the country calling code and `trunk` the national prefix dropped
// when a local number is turned into an international one — '0' nearly
// everywhere, '8' in Lithuania and Belarus, and none in Italy, whose numbers
// keep their leading 0.
interface Country {
  code: string;
  name: string;
  dial: string;
  trunk?: string;
}
const COUNTRIES: Country[] = [
  { code: 'LT', name: 'Lithuania', dial: '370', trunk: '8' },
  { code: 'LV', name: 'Latvia', dial: '371' },
  { code: 'EE', name: 'Estonia', dial: '372' },
  { code: 'PL', name: 'Poland', dial: '48' },
  { code: 'AL', name: 'Albania', dial: '355' },
  { code: 'AD', name: 'Andorra', dial: '376' },
  { code: 'AT', name: 'Austria', dial: '43' },
  { code: 'BY', name: 'Belarus', dial: '375', trunk: '8' },
  { code: 'BE', name: 'Belgium', dial: '32' },
  { code: 'BA', name: 'Bosnia and Herzegovina', dial: '387' },
  { code: 'BG', name: 'Bulgaria', dial: '359' },
  { code: 'HR', name: 'Croatia', dial: '385' },
  { code: 'CY', name: 'Cyprus', dial: '357' },
  { code: 'CZ', name: 'Czechia', dial: '420' },
  { code: 'DK', name: 'Denmark', dial: '45' },
  { code: 'FI', name: 'Finland', dial: '358' },
  { code: 'FR', name: 'France', dial: '33' },
  { code: 'DE', name: 'Germany', dial: '49' },
  { code: 'GR', name: 'Greece', dial: '30' },
  { code: 'HU', name: 'Hungary', dial: '36' },
  { code: 'IS', name: 'Iceland', dial: '354' },
  { code: 'IE', name: 'Ireland', dial: '353' },
  { code: 'IT', name: 'Italy', dial: '39', trunk: '' },
  { code: 'LI', name: 'Liechtenstein', dial: '423' },
  { code: 'LU', name: 'Luxembourg', dial: '352' },
  { code: 'MT', name: 'Malta', dial: '356' },
  { code: 'MD', name: 'Moldova', dial: '373' },
  { code: 'MC', name: 'Monaco', dial: '377' },
  { code: 'ME', name: 'Montenegro', dial: '382' },
  { code: 'NL', name: 'Netherlands', dial: '31' },
  { code: 'MK', name: 'North Macedonia', dial: '389' },
  { code: 'NO', name: 'Norway', dial: '47' },
  { code: 'PT', name: 'Portugal', dial: '351' },
  { code: 'RO', name: 'Romania', dial: '40' },
  { code: 'RS', name: 'Serbia', dial: '381' },
  { code: 'SK', name: 'Slovakia', dial: '421' },
  { code: 'SI', name: 'Slovenia', dial: '386' },
  { code: 'ES', name: 'Spain', dial: '34' },
  { code: 'SE', name: 'Sweden', dial: '46' },
  { code: 'CH', name: 'Switzerland', dial: '41' },
  { code: 'TR', name: 'Turkey', dial: '90' },
  { code: 'UA', name: 'Ukraine', dial: '380' },
  { code: 'GB', name: 'United Kingdom', dial: '44' },
];
// The four at the top of the list are the app's home region; the rest follow
// alphabetically in a second group.
const NEARBY_CODES = ['LT', 'LV', 'EE', 'PL'];
const countryName = (code: string) => COUNTRIES.find((c) => c.code === code)?.name || code;

/* ---------- phone numbers ----------
   OSM phone tags are free text — "+370 600 12345", "8 600 12345",
   "00370-600-12345", "(8~600) 12345", "+370 600 12345; +370 600 54321" — and
   the app dials the field as it is stored. So every number is reduced to one
   E.164 string (a '+' and digits, nothing else) before it reaches the
   database. Only the first number is kept: a dialler cannot use a list.
   Without a country to read a local number against, the number is cleaned of
   junk but left national rather than guessed at. */
function normalizePhone(raw: string, countryCode?: string): string {
  if (!raw) return '';
  // Split on list separators only: a slash belongs to numbers written like
  // "030 / 12 34 56", it does not separate two of them.
  const first = String(raw).split(/[;,\n]| or /i)[0] || '';
  let s = first.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, ''); // '+' only ever leads
  if (!s) return '';
  if (s.startsWith('00')) s = '+' + s.slice(2); // international access code
  if (s.startsWith('+')) return s.length >= 7 ? s : '';
  const c = COUNTRIES.find((x) => x.code === (countryCode || '').toUpperCase());
  if (!c) return s.length >= 6 ? s : '';
  // Already carries its country code (no '+' in front, e.g. "37060012345").
  if (s.startsWith(c.dial) && s.length >= c.dial.length + 6) return '+' + s;
  const trunk = c.trunk === undefined ? '0' : c.trunk;
  if (trunk && s.startsWith(trunk)) s = s.slice(trunk.length);
  return s.length >= 6 ? '+' + c.dial + s : '';
}

/* ---------- country outline (used by the Manage view) ----------
   Uploaded rows are plain coordinates with no country on them, so limiting
   that list means testing each point against the country's outline. One
   simplified polygon per country is fetched from Nominatim and reused. */
// A country's outline. `fallback` is set when it came from the coarser
// Natural Earth data (Nominatim unreachable) — see inCountry().
interface CountryShape {
  rings: number[][][];
  fallback?: { bbox: number[]; others: { bbox: number[]; rings: number[][][] }[] };
}
const countryShapeCache: Record<string, CountryShape> = {};

const outerRings = (g: any): number[][][] =>
  g?.type === 'Polygon'
    ? [g.coordinates[0]]
    : g?.type === 'MultiPolygon'
    ? g.coordinates.map((poly: number[][][]) => poly[0])
    : [];
async function countryShape(code: string): Promise<CountryShape> {
  if (countryShapeCache[code]) return countryShapeCache[code];
  let shape: CountryShape;
  try {
    const r = await fetch(
      'https://nominatim.openstreetmap.org/search?format=json&limit=1&polygon_geojson=1&polygon_threshold=0.02' +
        `&countrycodes=${code.toLowerCase()}&q=${encodeURIComponent(countryName(code))}`,
      { headers: { Accept: 'application/json' } }
    );
    if (!r.ok) throw new Error('HTTP ' + r.status);
    const rings = outerRings((await r.json())?.[0]?.geojson);
    if (!rings.length) throw new Error('no usable outline');
    shape = { rings };
  } catch (e) {
    // Nominatim down or rate-limited: the Natural Earth outline instead.
    const ne = await naturalEarthShape(code).catch(() => null);
    if (!ne) throw e;
    shape = ne;
  }
  countryShapeCache[code] = shape;
  return shape;
}

/* ---------- fallback country outlines (Natural Earth 1:10m) ----------
   A static TopoJSON on the jsDelivr CDN (world-atlas, ~0.8 MB gzipped,
   fetched once) for when Nominatim can't be reached. Countries there are
   keyed by ISO 3166-1 numeric code. */
const ISO_NUMERIC: Record<string, string> = {
  LT: '440', LV: '428', EE: '233', PL: '616', AL: '008', AD: '020', AT: '040', BY: '112',
  BE: '056', BA: '070', BG: '100', HR: '191', CY: '196', CZ: '203', DK: '208', FI: '246',
  FR: '250', DE: '276', GR: '300', HU: '348', IS: '352', IE: '372', IT: '380', LI: '438',
  LU: '442', MT: '470', MD: '498', MC: '492', ME: '499', NL: '528', MK: '807', NO: '578',
  PT: '620', RO: '642', RS: '688', SK: '703', SI: '705', ES: '724', SE: '752', CH: '756',
  TR: '792', UA: '804', GB: '826',
};
const ringsBbox = (rings: number[][][]) => {
  const b = [Infinity, Infinity, -Infinity, -Infinity]; // west, south, east, north
  for (const r of rings)
    for (const [x, y] of r) {
      if (x < b[0]) b[0] = x;
      if (y < b[1]) b[1] = y;
      if (x > b[2]) b[2] = x;
      if (y > b[3]) b[3] = y;
    }
  return b;
};
// Every country's outer rings, decoded once.
let worldAtlasPromise: Promise<Map<string, { bbox: number[]; rings: number[][][] }>> | null = null;
function loadWorldAtlas() {
  worldAtlasPromise =
    worldAtlasPromise ||
    fetch('https://cdn.jsdelivr.net/npm/world-atlas@2/countries-10m.json')
      .then((r) => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then((topo) => {
        // TopoJSON: delta-encoded, quantised arcs; a polygon ring is a list of
        // arc indexes (~i = arc i reversed) whose shared end points are dropped.
        const { scale, translate } = topo.transform;
        const arcs: number[][][] = topo.arcs.map((a: number[][]) => {
          let x = 0;
          let y = 0;
          return a.map(([dx, dy]) => {
            x += dx;
            y += dy;
            return [x * scale[0] + translate[0], y * scale[1] + translate[1]];
          });
        });
        const arc = (i: number) => (i < 0 ? arcs[~i].slice().reverse() : arcs[i]);
        // A ring that crosses the 180° meridian (Russia's Chukotka) would
        // wrap round the whole map in flat lon/lat and swallow e.g. northern
        // Norway — its western longitudes are moved past +180 instead.
        const unwrap = (r: number[][]) =>
          r.some((p, i) => i > 0 && Math.abs(p[0] - r[i - 1][0]) > 180)
            ? r.map(([x, y]) => [x < 0 ? x + 360 : x, y])
            : r;
        const ring = (idx: number[]) => unwrap(idx.flatMap((i, n) => (n ? arc(i).slice(1) : arc(i))));
        const all = new Map<string, { bbox: number[]; rings: number[][][] }>();
        topo.objects.countries.geometries.forEach((g: any, n: number) => {
          const polys = g.type === 'Polygon' ? [g.arcs] : g.type === 'MultiPolygon' ? g.arcs : [];
          const rings = polys.map((p: number[][]) => ring(p[0]));
          if (rings.length) all.set(g.id != null ? String(g.id) : `_${n}`, { bbox: ringsBbox(rings), rings });
        });
        return all;
      })
      .catch((e) => {
        worldAtlasPromise = null; // let a later call retry
        throw e;
      });
  return worldAtlasPromise;
}
async function naturalEarthShape(code: string): Promise<CountryShape | null> {
  const id = ISO_NUMERIC[code];
  if (!id) return null;
  const all = await loadWorldAtlas();
  const me = all.get(id);
  if (!me) return null;
  const others = Array.from(all.entries())
    .filter(([k]) => k !== id)
    .map(([, v]) => v);
  return { rings: me.rings, fallback: { bbox: me.bbox, others } };
}

// Natural Earth cuts small islands, spits and harbours out of the coastline,
// so with that outline a point also counts when it lies within this distance
// of the country's edge and inside no other country (the sea, an islet).
const COAST_SLACK_KM = 5;
function distToRingsKm(lat: number, lon: number, rings: number[][][], stopBelow: number) {
  const kx = 111.32 * Math.cos((lat * Math.PI) / 180);
  const ky = 110.57;
  let best = Infinity;
  for (const r of rings)
    for (let i = 1; i < r.length; i++) {
      const ax = (r[i - 1][0] - lon) * kx;
      const ay = (r[i - 1][1] - lat) * ky;
      const bx = (r[i][0] - lon) * kx;
      const by = (r[i][1] - lat) * ky;
      const dx = bx - ax;
      const dy = by - ay;
      const len = dx * dx + dy * dy;
      const t = len ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / len)) : 0;
      const d = Math.hypot(ax + t * dx, ay + t * dy);
      if (d < best && (best = d) < stopBelow) return best;
    }
  return best;
}
const inBbox = (b: number[], lat: number, lon: number, padDeg = 0) =>
  lon >= b[0] - padDeg && lat >= b[1] - padDeg && lon <= b[2] + padDeg && lat <= b[3] + padDeg;
function inCountry(lat: number, lon: number, shape: CountryShape): boolean {
  if (pointInRings(lat, lon, shape.rings)) return true;
  const f = shape.fallback;
  if (!f) return false;
  const pad = COAST_SLACK_KM / 30; // degrees; generous enough for longitude up to ~75° N
  if (!inBbox(f.bbox, lat, lon, pad)) return false;
  if (distToRingsKm(lat, lon, shape.rings, COAST_SLACK_KM) > COAST_SLACK_KM) return false;
  return !f.others.some((o) => inBbox(o.bbox, lat, lon) && pointInRings(lat, lon, o.rings));
}

/* ---------- place search with a fallback ----------
   Nominatim (openstreetmap.org) first; when it fails or is rate-limited,
   Photon (photon.komoot.io), which serves the same OSM data. */
type GeoHit = { bbox: [number, number, number, number]; name: string }; // [south, north, west, east]
async function geoSearch(q: string, countryCode?: string): Promise<GeoHit | null> {
  try {
    const r = await fetch(
      'https://nominatim.openstreetmap.org/search?format=json&limit=1' +
        (countryCode ? `&countrycodes=${countryCode.toLowerCase()}` : '') +
        `&q=${encodeURIComponent(q)}`,
      { headers: { Accept: 'application/json' } }
    );
    if (!r.ok) throw new Error('HTTP ' + r.status);
    const j = await r.json();
    if (!j.length) return null;
    return { bbox: j[0].boundingbox.map(Number), name: String(j[0].display_name).split(',')[0] };
  } catch {
    const r = await fetch(
      `https://photon.komoot.io/api/?limit=5&lang=en&q=${encodeURIComponent(q)}` +
        (countryCode ? '&osm_tag=place:country' : '')
    );
    if (!r.ok) throw new Error('both Nominatim and Photon failed (Photon HTTP ' + r.status + ')');
    const feats: any[] = (await r.json()).features || [];
    const f = countryCode ? feats.find((x) => x.properties?.countrycode === countryCode) : feats[0];
    if (!f) return null;
    const ext = f.properties?.extent; // [west, north, east, south]
    const [lon, lat] = f.geometry.coordinates;
    return {
      bbox: ext ? [ext[3], ext[1], ext[0], ext[2]] : [lat - 0.05, lat + 0.05, lon - 0.05, lon + 0.05],
      name: f.properties?.name || q,
    };
  }
}

// Ray casting against the outer rings ([lon,lat] pairs). Holes are ignored, so
// an enclave inside a country counts as inside it — close enough for a list
// filter, and it never drops a point that really is in the country.
function pointInRings(lat: number, lon: number, rings: number[][][]): boolean {
  return rings.some((ring) => {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i];
      const [xj, yj] = ring[j];
      if (yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  });
}

// Enum values first, then anything already present in the data — a custom or
// older label stays visible and tickable instead of being silently dropped.
function mergeOptions(base: string[], seen: string[]): string[] {
  const out = [...base];
  seen.forEach((v) => {
    if (v && !out.includes(v)) out.push(v);
  });
  return out;
}

function deriveServices(t: OsmTags): string[] {
  const blob = tagText(t);
  return Array.from(new Set(SERVICE_FACETS.filter((f) => f.test(t, blob)).map((f) => f.label)));
}
function deriveActivities(t: OsmTags): string[] {
  const blob = tagText(t);
  return Array.from(new Set(ACTIVITY_FACETS.filter((f) => f.test(t, blob)).map((f) => f.label)));
}
function derivePayment(t: OsmTags) {
  if (t.fee === 'no' || t.fee === 'free') return 'Free';
  if (t.fee === 'yes' || t.charge) return 'Paid';
  return 'Unknown';
}
function placeTypeFor(t: OsmTags) {
  for (const c of CATEGORIES) {
    if (c.match(t)) return c.placeType;
  }
  // Fallback so activity/service-only results (e.g. a beach or swimming area
  // fetched via "Swimming available") aren't all left as a bare "Other".
  if (
    t.natural === 'beach' ||
    t.natural === 'water' ||
    t.leisure === 'swimming_area' ||
    t.leisure === 'beach_resort' ||
    t.leisure === 'park' ||
    t.leisure === 'nature_reserve' ||
    t.tourism === 'attraction'
  )
    return 'Place in nature';
  return 'Other';
}

// Reverse-geocode one point to a "town, region, country" hint via Nominatim.
// Called once per search (on the results' centroid) to stay within usage limits.
// Falls back to Photon when Nominatim fails.
async function reverseGeocodeRegion(lat: number, lon: number): Promise<string> {
  const join = (parts: any[]) => Array.from(new Set(parts.filter(Boolean))).join(', ');
  try {
    const r = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&zoom=10&lat=${lat}&lon=${lon}`,
      { headers: { Accept: 'application/json' } }
    );
    if (!r.ok) throw new Error('HTTP ' + r.status);
    const a = (await r.json()).address || {};
    return join([a.city || a.town || a.village || a.municipality || a.county, a.state || a.region, a.country]);
  } catch {
    try {
      const r = await fetch(`https://photon.komoot.io/reverse?lang=en&lat=${lat}&lon=${lon}`);
      const p = (await r.json()).features?.[0]?.properties || {};
      return join([p.city || p.county, p.state, p.country]);
    } catch {
      return '';
    }
  }
}

/* ---------- map tiles with mirrors ----------
   openstreetmap.org first; after a run of failed tiles with none loading in
   between, the layer is swapped for the next server (all render OSM data). */
const TILE_SERVERS = [
  { url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png', subdomains: 'abc', attribution: '© OpenStreetMap' },
  { url: 'https://tile.openstreetmap.de/{z}/{x}/{y}.png', subdomains: 'abc', attribution: '© OpenStreetMap' },
  {
    url: 'https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png',
    subdomains: 'abc',
    attribution: '© OpenStreetMap France',
  },
  {
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png',
    subdomains: 'abcd',
    attribution: '© OpenStreetMap © CARTO',
  },
];
const TILE_FAILS_BEFORE_SWITCH = 4;
function addTileLayerWithFallback(L: any, map: any, i = 0) {
  const s = TILE_SERVERS[i];
  const layer = L.tileLayer(s.url, { maxZoom: 19, subdomains: s.subdomains, attribution: s.attribution });
  let fails = 0;
  layer.on('tileload', () => (fails = 0));
  layer.on('tileerror', () => {
    if (++fails < TILE_FAILS_BEFORE_SWITCH || i + 1 >= TILE_SERVERS.length) return;
    fails = -Infinity; // switch once
    map.removeLayer(layer);
    console.warn(`[map] tiles from ${new URL(s.url.replace('{s}', 'a')).hostname} failing — switching to the next server`);
    addTileLayerWithFallback(L, map, i + 1);
  });
  layer.addTo(map);
}

/* ---------- dynamic Leaflet loader (only when the panel mounts) ---------- */
let leafletPromise: Promise<any> | null = null;
function loadLeaflet(): Promise<any> {
  if ((window as any).L) return Promise.resolve((window as any).L);
  if (leafletPromise) return leafletPromise;
  leafletPromise = new Promise((resolve, reject) => {
    if (!document.querySelector('link[data-leaflet]')) {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      css.setAttribute('data-leaflet', '');
      document.head.appendChild(css);
    }
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.async = true;
    script.onload = () => resolve((window as any).L);
    script.onerror = () => reject(new Error('Failed to load Leaflet'));
    document.body.appendChild(script);
  });
  return leafletPromise;
}

/* ============================================================
   Component
   ============================================================ */

/* ---------- local draft: the working set survives closing the browser ----------
   Everything in the results table is mirrored into localStorage, so a closed
   tab, a reload or a crash can't lose rows that were never uploaded. The
   _uploaded flags are saved too: a restored row that already went to the
   database still shows as uploaded and is never sent twice.
   localStorage (not IndexedDB) on purpose — its writes are synchronous, which
   is what makes the flush on page-close below reliable. */
const DRAFT_KEY = 'cl_admin_draft_v1';

interface AdminDraft {
  savedAt: number;
  locations: PlaceItem[];
  region: string;
  noteLabels: string[];
  serviceOnlyResult: boolean;
  forcePlaceType: string | null;
  quickFilter: string;
  payFilter: string;
  country: string;
}

function loadDraft(): AdminDraft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d || !Array.isArray(d.locations) || !d.locations.length) return null;
    return d as AdminDraft;
  } catch {
    return null; // unreadable / corrupt draft — start clean rather than crash
  }
}

// Returns '' on success, or a short reason the draft could not be stored.
function saveDraft(d: AdminDraft): string {
  try {
    if (!d.locations.length) {
      localStorage.removeItem(DRAFT_KEY);
      return '';
    }
    localStorage.setItem(DRAFT_KEY, JSON.stringify(d));
    return '';
  } catch (e: any) {
    // Usually the 5 MB quota. Retry without the raw OSM tags: they are only
    // the source the row's own fields were already derived from, so dropping
    // them keeps every edit while roughly halving the payload.
    try {
      const slim = d.locations.map((l) => ({ ...l, _osm: { ...l._osm, tags: {} } }));
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...d, locations: slim }));
      return '';
    } catch (e2: any) {
      return e2?.name === 'QuotaExceededError' || e?.name === 'QuotaExceededError'
        ? 'browser storage is full'
        : e2?.message || e?.message || 'storage unavailable';
    }
  }
}

const AdminPanel: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  // Read the previous session's draft once, and seed the state below from it.
  const initialDraftRef = useRef<AdminDraft | null | undefined>(undefined);
  if (initialDraftRef.current === undefined) initialDraftRef.current = loadDraft();
  const initialDraft = initialDraftRef.current;

  const [locations, setLocations] = useState<PlaceItem[]>(() => initialDraft?.locations ?? []);
  const [status, setStatusState] = useState<{ msg: string; cls: string }>({ msg: 'Idle.', cls: '' });
  const [searching, setSearching] = useState(false);
  // demo = everything stays local (export only); production = the upload
  // button and the Manage view write to the LIVE Firebase database.
  const [mode, setMode] = useState<'demo' | 'production'>(
    () => (localStorage.getItem('cl_admin_mode') === 'production' ? 'production' : 'demo')
  );
  const [view, setView] = useState<'import' | 'manage' | 'users'>('import');
  const [uploading, setUploading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [gptField, setGptField] = useState('description');
  // Custom AI prompts, organised as prompt SETS: one "Default" set plus one per
  // place type. promptSets[set][field] holds only the fields the user edited for
  // that set. Resolution when generating (see effectivePrompt): the location's
  // place-type set → the Default set → the built-in FIELD_PROMPTS default.
  const [promptSets, setPromptSets] = useState<Record<string, Record<string, string>>>({});
  const [promptSet, setPromptSet] = useState(PLACETYPE_ENUM[0]); // which set the editor shows
  const [showPrompts, setShowPrompts] = useState(false);
  // Persistence of prompts to the signed-in user's account (Firebase RTDB).
  const [promptsSaved, setPromptsSaved] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const promptsLoadedRef = useRef(false); // guards auto-save until the load finishes
  // Per service / activity extra note. A location can have many at once, so
  // (unlike place types) these aren't full field sets — each is one instruction
  // appended to the DESCRIPTION prompt of any location that has that service /
  // activity. Keyed by the exact enum label; empty entries are removed.
  const [facetHints, setFacetHints] = useState<Record<string, string>>({});
  const [region, setRegion] = useState(() => initialDraft?.region ?? '');
  const [cats, setCats] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(CATEGORIES.map((c) => [c.id, false]))
  );
  // Optional "By services" / "By activities" narrowing (applied after fetch,
  // searching every OSM field — see SERVICE_FACETS / ACTIVITY_FACETS).
  const [svcSel, setSvcSel] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(SERVICE_FACETS.map((f) => [f.id, false]))
  );
  const [actSel, setActSel] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(ACTIVITY_FACETS.map((f) => [f.id, false]))
  );
  const [quickFilter, setQuickFilter] = useState(() => initialDraft?.quickFilter ?? '');
  // The table filters behind the typing, so a big working set doesn't stall it.
  const quickFilterDeferred = useDeferredValue(quickFilter);
  const [payFilter, setPayFilter] = useState(() => initialDraft?.payFilter ?? '');
  // '' = no country limit: the search then uses the map view alone.
  const [country, setCountry] = useState(() => initialDraft?.country ?? '');
  const [editing, setEditing] = useState<{ id: string; field: string } | null>(null);
  // Tracked by row id (not a snapshot) so reordering shows up immediately.
  const [lightboxId, setLightboxId] = useState<string | null>(null);
  const [preview, setPreview] = useState('// Search and your mapped JSON will appear here.');
  const [photoSrc, setPhotoSrc] = useState('satellite');
  const [satMeters, setSatMeters] = useState(200);
  // Where search results come from: OSM with Overture layered on top, or
  // either one alone. Saved with the import defaults.
  const [dataSource, setDataSource] = useState<DataSource>('both');
  const [ovtMinConf, setOvtMinConf] = useState(0.7);
  // GPT model for the Generate buttons. Saved with the import defaults.
  const [aiModel, setAiModel] = useState(DEFAULT_AI_MODEL);
  // Measured AI cost per model, re-read after each generate run.
  const [aiUsage, setAiUsage] = useState(getAIUsage);
  const [satPreview, setSatPreview] = useState<string | null>(null);
  // Local-draft status shown next to the results count, plus the always-current
  // snapshot the close handler flushes.
  const [draftState, setDraftState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [draftErr, setDraftErr] = useState('');
  const draftRef = useRef<AdminDraft>({
    savedAt: 0,
    locations: initialDraft?.locations ?? [],
    region: initialDraft?.region ?? '',
    noteLabels: initialDraft?.noteLabels ?? [],
    serviceOnlyResult: initialDraft?.serviceOnlyResult ?? false,
    forcePlaceType: initialDraft?.forcePlaceType ?? null,
    quickFilter: initialDraft?.quickFilter ?? '',
    payFilter: initialDraft?.payFilter ?? '',
    country: initialDraft?.country ?? '',
  });

  const setStatus = (msg: string, cls = '') => setStatusState({ msg, cls });

  // refs for "Import defaults" / option inputs (read at action time, like the original)
  const placeNameRef = useRef<HTMLInputElement>(null);
  const osmFeeRef = useRef<HTMLSelectElement>(null);
  const limitRef = useRef<HTMLInputElement>(null);
  const defAddedByRef = useRef<HTMLInputElement>(null);
  const defRatingRef = useRef<HTMLInputElement>(null);
  const defActivitiesRef = useRef<HTMLInputElement>(null);
  const defServicesRef = useRef<HTMLInputElement>(null);
  const gmapsKeyRef = useRef<HTMLInputElement>(null);
  const svVerifyRef = useRef<HTMLInputElement>(null);
  const defUploadedByRef = useRef<HTMLInputElement>(null);
  const keyModeRef = useRef<HTMLSelectElement>(null);
  const wrapModeRef = useRef<HTMLSelectElement>(null);

  // bulk action inputs
  const bulkFieldRef = useRef<HTMLSelectElement>(null);
  const bulkValueRef = useRef<HTMLInputElement>(null);
  // Bulk add/remove picks from the same checkbox list as the cells do.
  const [arrField, setArrField] = useState<'services' | 'activities'>('services');
  const [arrPick, setArrPick] = useState<string[]>([]);
  const [arrPickOpen, setArrPickOpen] = useState(false);

  // Leaflet
  const mapDivRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<Record<string, any>>({});

  // Set when a Services parent governs a search (services picked, no place type):
  // every result is prefilled with this place type instead of the OSM-derived
  // one, and AI generation then leaves place type untouched.
  const forcePlaceTypeRef = useRef<string | null>(initialDraft?.forcePlaceType ?? null);
  const [serviceOnlyResult, setServiceOnlyResult] = useState(() => initialDraft?.serviceOnlyResult ?? false);
  // Service/activity labels whose notes apply to this search's descriptions —
  // the ticked checkboxes of the governing parent (see search()).
  const [noteLabels, setNoteLabels] = useState<string[]>(() => initialDraft?.noteLabels ?? []);

  /* ---------- map init (start: Lithuania) ---------- */
  useEffect(() => {
    let cancelled = false;
    loadLeaflet().then((L) => {
      if (cancelled || !mapDivRef.current || mapRef.current) return;
      const map = L.map(mapDivRef.current).setView([55.3, 23.9], 7);
      addTileLayerWithFallback(L, map);
      mapRef.current = map;
    });
    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  /* ---------- load this user's saved prompts on login ---------- */
  useEffect(() => {
    const uid = user?.uid;
    promptsLoadedRef.current = false;
    if (!uid) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await get(ref(db, `users/${uid}/aiPromptPresets`));
        if (!cancelled && snap.exists()) {
          const raw = snap.val();
          const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
          if (data && typeof data === 'object') {
            setPromptSets(data.promptSets || {});
            setFacetHints(data.facetHints || {});
          }
        }
      } catch {
        /* ignore — fall back to in-memory defaults */
      } finally {
        if (!cancelled) promptsLoadedRef.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  /* ---------- auto-save prompts to this user's account (debounced) ---------- */
  useEffect(() => {
    const uid = user?.uid;
    if (!uid || !promptsLoadedRef.current) return; // don't overwrite before load
    setPromptsSaved('saving');
    const t = setTimeout(() => {
      set(ref(db, `users/${uid}/aiPromptPresets`), JSON.stringify({ promptSets, facetHints }))
        .then(() => setPromptsSaved('saved'))
        .catch(() => setPromptsSaved('error'));
    }, 800);
    return () => clearTimeout(t);
  }, [promptSets, facetHints, user?.uid]);

  /* ---------- Import defaults persistence (per signed-in user) ----------
     The "3 · Import defaults" inputs stay uncontrolled refs (read at action
     time, like the rest of the form) — persistence just syncs their DOM
     value on load and reads it back on change, the same load/debounced-save
     shape as the prompts above but without lifting everything to state. */
  const importDefaultsLoadedRef = useRef(false);
  const importSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [importDefaultsSaved, setImportDefaultsSaved] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  useEffect(() => {
    const uid = user?.uid;
    importDefaultsLoadedRef.current = false;
    if (!uid) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await get(ref(db, `users/${uid}/importDefaults`));
        if (!cancelled && snap.exists()) {
          const raw = snap.val();
          const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
          if (data && typeof data === 'object') {
            if (defAddedByRef.current) defAddedByRef.current.value = data.addedBy ?? DEFAULT_UPLOADER;
            if (defRatingRef.current) defRatingRef.current.value = String(data.rating ?? 0);
            if (defActivitiesRef.current) defActivitiesRef.current.value = data.activities ?? '';
            if (defServicesRef.current) defServicesRef.current.value = data.services ?? '';
            if (typeof data.photoSrc === 'string') setPhotoSrc(data.photoSrc);
            if (typeof data.satMeters === 'number') setSatMeters(data.satMeters);
            if (['both', 'osm', 'overture'].includes(data.dataSource)) setDataSource(data.dataSource);
            if (typeof data.ovtMinConf === 'number') setOvtMinConf(data.ovtMinConf);
            if (AI_MODELS.some((m) => m.id === data.aiModel)) setAiModel(data.aiModel);
            if (defUploadedByRef.current) defUploadedByRef.current.value = data.uploadedBy ?? DEFAULT_UPLOADER;
            if (keyModeRef.current) keyModeRef.current.value = data.keyMode ?? 'push';
            if (wrapModeRef.current) wrapModeRef.current.value = data.wrapMode ?? 'places';
            // The Street View key/checkbox live in a DOM node that's kept
            // mounted (just hidden) specifically so this ref write always
            // lands even when Satellite is the active photo source.
            if (gmapsKeyRef.current) gmapsKeyRef.current.value = data.gmapsKey ?? '';
            if (svVerifyRef.current) svVerifyRef.current.checked = !!data.svVerify;
            refreshPreview();
          }
        }
      } catch {
        /* ignore — fall back to in-memory defaults */
      } finally {
        if (!cancelled) importDefaultsLoadedRef.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
    // refreshPreview intentionally omitted — it changes with every search
    // result, and re-running this effect would re-fetch on each of those.
  }, [user?.uid]);

  const scheduleSaveImportDefaults = useCallback(() => {
    const uid = user?.uid;
    if (!uid || !importDefaultsLoadedRef.current) return; // don't overwrite before load
    const data = {
      addedBy: defAddedByRef.current?.value ?? DEFAULT_UPLOADER,
      rating: Number(defRatingRef.current?.value) || 0,
      activities: defActivitiesRef.current?.value ?? '',
      services: defServicesRef.current?.value ?? '',
      photoSrc,
      satMeters,
      dataSource,
      ovtMinConf,
      aiModel,
      gmapsKey: gmapsKeyRef.current?.value ?? '',
      svVerify: svVerifyRef.current?.checked ?? false,
      uploadedBy: defUploadedByRef.current?.value ?? DEFAULT_UPLOADER,
      keyMode: keyModeRef.current?.value ?? 'push',
      wrapMode: wrapModeRef.current?.value ?? 'places',
    };
    setImportDefaultsSaved('saving');
    if (importSaveTimerRef.current) clearTimeout(importSaveTimerRef.current);
    importSaveTimerRef.current = setTimeout(() => {
      set(ref(db, `users/${uid}/importDefaults`), JSON.stringify(data))
        .then(() => setImportDefaultsSaved('saved'))
        .catch(() => setImportDefaultsSaved('error'));
    }, 800);
  }, [photoSrc, satMeters, dataSource, ovtMinConf, aiModel, user?.uid]);

  // photoSrc/satMeters/dataSource/ovtMinConf are already React state (their
  // own onChange doesn't touch the refs above), so they need their own save trigger.
  useEffect(() => {
    scheduleSaveImportDefaults();
  }, [photoSrc, satMeters, dataSource, ovtMinConf, aiModel]);

  // generateField reads the model from openai.ts, not from a parameter.
  useEffect(() => {
    setAIModel(aiModel);
  }, [aiModel]);
  // A finished generate run has new token counts to show.
  useEffect(() => {
    if (!generating) setAiUsage(getAIUsage());
  }, [generating]);

  /* ---------- map an OSM element to a place object ---------- */
  const mapElement = useCallback((el: any): PlaceItem | null => {
    const t: OsmTags = el.tags || {};
    const lat = el.lat ?? el.center?.lat;
    const lon = el.lon ?? el.center?.lon;
    if (lat == null || lon == null) return null;
    const pt = forcePlaceTypeRef.current || placeTypeFor(t);
    return {
      _id: `osm_${el.type}_${el.id}`,
      _sel: false,
      _osm: { type: el.type, id: el.id, tags: t },
      activities: mergeList(deriveActivities(t), parseList(defActivitiesRef.current?.value || '')),
      addedBy: defAddedByRef.current?.value || DEFAULT_UPLOADER,
      // Left blank on purpose: the record's date is the day it actually
      // reaches the database, not the day it was pulled out of OSM. A row can
      // sit in the table (and in the saved draft) for days before upload, so
      // both date fields are stamped in uploadToFirebase / toExport instead.
      dateAdded: '',
      description: t.name || t.description || t.operator || `${pt} (OSM ${el.id})`,
      email: t.email || t['contact:email'] || '',
      heightLimit: t.maxheight || t['maxheight:physical'] || '',
      images: {},
      lastUpdatedCostsDate: '',
      latitude: +(+lat).toFixed(6),
      longitude: +(+lon).toFixed(6),
      paymentStatus: derivePayment(t),
      phone: t.phone || t['contact:phone'] || '',
      placeType: pt,
      rating: Number(defRatingRef.current?.value) || 0,
      services: mergeList(deriveServices(t), parseList(defServicesRef.current?.value || '')),
      website: t.website || t['contact:website'] || t.url || '',
    };
  }, []);

  /* ---------- build Overpass query for current bounds + selected cats ---------- */
  const buildQuery = useCallback(() => {
    const map = mapRef.current;
    if (!map) return null;
    const b = map.getBounds();
    const bbox = `(${b.getSouth().toFixed(5)},${b.getWest().toFixed(5)},${b.getNorth().toFixed(5)},${b.getEast().toFixed(5)})`;
    // Places drive the fetch when picked (services/activities then narrow the
    // results — giving "campsites WITH showers" AND-semantics). With no place
    // picked, the chosen services/activities drive the fetch themselves.
    const placeSel = CATEGORIES.filter((c) => cats[c.id]).flatMap((c) => c.sel);
    const facetSel = [
      ...SERVICE_FACETS.filter((f) => svcSel[f.id]),
      ...ACTIVITY_FACETS.filter((f) => actSel[f.id]),
    ].flatMap((f) => f.sel);
    const selectors = placeSel.length ? placeSel : facetSel;
    if (selectors.length === 0) return null;
    const feeVal = osmFeeRef.current?.value || '';
    const fee = feeVal ? `["fee"="${feeVal}"]` : '';
    // A chosen country becomes an OSM area every statement is intersected
    // with, so places outside it are never fetched in the first place. Area
    // queries are heavier, hence the longer server-side timeout.
    const head = country ? `area["ISO3166-1"="${country}"][admin_level=2]->.cc;\n` : '';
    const inCountry = country ? '(area.cc)' : '';
    const parts: string[] = [];
    selectors.forEach((s) => {
      parts.push(`  node${s}${fee}${inCountry}${bbox};`);
      parts.push(`  way${s}${fee}${inCountry}${bbox};`);
      parts.push(`  relation${s}${fee}${inCountry}${bbox};`);
    });
    return `[out:json][timeout:${country ? 60 : 30}];\n${head}(\n${parts.join('\n')}\n);\nout center tags ${
      Number(limitRef.current?.value) || 300
    };`;
  }, [cats, svcSel, actSel, country]);

  /* ---------- export ---------- */
  const visibleRows = useCallback(() => {
    const f = quickFilterDeferred.trim().toLowerCase();
    const pay = payFilter;
    return locations.filter((l) => {
      // Rows fetched under another country (or before a country was chosen)
      // stay in the working set but out of sight until the filter matches.
      if (country && l._country !== country) return false;
      if (pay && l.paymentStatus !== pay) return false;
      if (
        f &&
        !((l.description || '').toLowerCase().includes(f) || (l.placeType || '').toLowerCase().includes(f))
      )
        return false;
      return true;
    });
  }, [locations, quickFilterDeferred, payFilter, country]);

  // `limit` = only the first N rows (the on-screen preview); exports take all.
  const toExport = useCallback((limit?: number) => {
    const keyMode = keyModeRef.current?.value || 'push';
    const out: Record<string, any> = {};
    const rows = visibleRows();
    (limit === undefined ? rows : rows.slice(0, limit)).forEach((l) => {
      const key = keyMode === 'push' ? pushId() : l._id;
      const { _id, _sel, _osm, _uploaded, _dupWarning, _photoWarning, _country, _src, ...clean } = l as any;
      // Same rule as the upload: this file is what goes into the database, so
      // it carries the date it was produced rather than the fetch date.
      const now = fmtDateSlash(new Date());
      clean.dateAdded = now;
      clean.lastUpdatedCostsDate = now;
      // Photo entries carry the same moment, so nothing in the file is dated
      // by the OSM fetch.
      const imgs: Record<string, ImageEntry> = {};
      Object.entries((clean.images || {}) as Record<string, ImageEntry>).forEach(([k, img]) => {
        imgs[k] = { ...img, dateUploaded: now };
      });
      clean.images = imgs;
      out[key] = clean;
    });
    return wrapModeRef.current?.value === 'places' ? { places: out } : out;
  }, [visibleRows]);

  // Re-runs on every edit, so it stringifies a sample, not thousands of rows.
  const PREVIEW_ROWS = 20;
  const refreshPreview = useCallback(() => {
    const total = visibleRows().length;
    setPreview(
      JSON.stringify(toExport(PREVIEW_ROWS), null, 2) +
        (total > PREVIEW_ROWS
          ? `\n\n// Preview: first ${PREVIEW_ROWS} of ${total} locations — the downloaded / copied file has all of them.`
          : '')
    );
  }, [toExport, visibleRows]);

  /* ---------- markers ---------- */
  const drawMarkers = useCallback((locs: PlaceItem[]) => {
    const L = (window as any).L;
    const map = mapRef.current;
    if (!L || !map) return;
    Object.values(markersRef.current).forEach((m: any) => map.removeLayer(m));
    markersRef.current = {};
    const pts: [number, number][] = [];
    locs.forEach((l) => {
      const m = L.circleMarker([l.latitude, l.longitude], {
        radius: 6,
        color: '#689f38',
        weight: 2,
        fillColor: '#7cb342',
        fillOpacity: 0.6,
      }).bindPopup(`<b>${escapeHtml(l.description)}</b><br>${escapeHtml(l.placeType)}`);
      m.addTo(map);
      markersRef.current[l._id] = m;
      pts.push([l.latitude, l.longitude]);
    });
    if (pts.length) {
      try {
        map.fitBounds(pts, { padding: [30, 30], maxZoom: 13 });
      } catch {
        /* ignore */
      }
    }
  }, []);

  /* ---------- run the search ---------- */
  const search = useCallback(async () => {
    const useOsm = dataSource !== 'overture';
    const useOvt = dataSource !== 'osm';
    const pickedCats = CATEGORIES.filter((c) => cats[c.id]);
    const ovtCats = new Set(pickedCats.flatMap((c) => c.ovt));
    const q = useOsm ? buildQuery() : null;
    if (useOsm && !q) {
      setStatus('Pick at least one filter (a place, service or activity).', 'err');
      return;
    }
    if (!useOsm && !ovtCats.size) {
      setStatus(
        pickedCats.length
          ? `Overture has no equivalent of: ${pickedCats.map((c) => c.label).join(', ')}. Pick another place type or switch "Data sources" to OSM.`
          : 'Overture only: pick at least one place type — Overture has no services/activities data.',
        'err'
      );
      return;
    }
    setSearching(true);
    let data: any = { elements: [] };
    let err: string | null = null;
    if (useOsm) {
      // Each server gets a hard timeout; a busy or failing one hands over to
      // the next mirror, and the status line says which one is being asked.
      // The country variant asks Overpass for [timeout:60], so give it longer.
      const hardMs = country ? 65000 : 35000;
      const res = await overpassFetch(q as string, hardMs, (host, n, of) =>
        setStatus(
          `${useOvt ? 'Layer 1/2 · ' : ''}Querying OpenStreetMap${n > 1 ? ` — previous server busy, trying mirror ${n}/${of}` : ''} (${host})…`
        )
      );
      data = res.data;
      err = res.err;
    }
    if (!data) {
      setSearching(false);
      setStatus(
        `Search stopped: ${err || 'unknown error'}. Wait a minute and press Search again, or use a smaller area / fewer categories.`,
        'err'
      );
      return;
    }

    // Governing "parent" filter by priority: Places > Services > Activities.
    // It decides both the place type and which facet notes apply.
    const anyPlace = CATEGORIES.some((c) => cats[c.id]);
    const selSvc = SERVICE_FACETS.filter((f) => svcSel[f.id]).map((f) => f.label);
    const selAct = ACTIVITY_FACETS.filter((f) => actSel[f.id]).map((f) => f.label);
    const parent = anyPlace ? 'places' : selSvc.length ? 'services' : selAct.length ? 'activities' : 'none';
    // A Services parent (no place picked) forces the "Services" place type and
    // its field set; placeType is then not regenerated by AI.
    forcePlaceTypeRef.current = parent === 'services' ? 'Services' : null;
    setServiceOnlyResult(parent === 'services');
    // Notes appended to every result's description come from the governing
    // parent's ticked checkboxes (a Places parent adds none).
    setNoteLabels(parent === 'services' ? selSvc : parent === 'activities' ? selAct : []);

    const mapped = ((data.elements as any[]) || []).map(mapElement).filter(Boolean) as PlaceItem[];
    const seen = new Set<string>();
    let deduped: PlaceItem[] = [];
    for (const m of mapped) {
      if (!seen.has(m._id)) {
        seen.add(m._id);
        deduped.push(m);
      }
    }
    deduped.forEach((l) => (l._src = 'osm'));

    // Layer 2: Overture checks the OSM results. An OSM place is kept only if
    // Overture has the same place (which also fills its empty contact fields);
    // Overture places OSM doesn't have are not added.
    let ovtNote = '';
    if (useOsm && useOvt && !ovtCats.size) {
      setSearching(false);
      setStatus(
        `OSM + Overture: Overture has no equivalent of ${
          pickedCats.length ? pickedCats.map((c) => c.label).join(', ') : 'services/activities'
        }, so no OSM place can be confirmed. Pick another place type or switch "Data sources" to OSM only.`,
        'err'
      );
      return;
    } else if (useOvt && !ovtCats.size) {
      ovtNote = ` Overture layer skipped — ${
        pickedCats.length ? 'it has no equivalent of the picked place types' : 'it has no services/activities data'
      }.`;
    } else if (useOvt) {
      const osmCount = deduped.length;
      try {
        // Loaded on first use — the Parquet reader stays out of the main bundle.
        const { fetchOverturePlaces, similarNames } = await import('./overture');
        const bb = mapRef.current.getBounds();
        const { release, places } = await fetchOverturePlaces(
          { w: bb.getWest(), s: bb.getSouth(), e: bb.getEast(), n: bb.getNorth() },
          ovtCats,
          {
            minConfidence: ovtMinConf,
            onProgress: (m) => setStatus((useOsm ? `Layer 2/2 · ${osmCount} from OSM · ` : '') + m),
          }
        );
        // With OSM on, its rows are already cut to the country server-side and
        // Overture only has to match them — the simplified outline would just
        // drop border places (and a Nominatim hiccup would fail the search).
        const shape = country && !useOsm ? await countryShape(country) : null;
        const inArea = shape ? places.filter((p) => inCountry(p.lat, p.lon, shape)) : places;
        const limit = Number(limitRef.current?.value) || 300;
        const osmRows = deduped.slice(0, osmCount);
        const enriched = new Set<string>();
        let added = 0;
        let overLimit = 0;
        for (const p of inArea) {
          let best: PlaceItem | null = null;
          let bestD = Infinity;
          for (const l of osmRows) {
            const d = haversineMeters(p.lat, p.lon, l.latitude, l.longitude);
            if (d < bestD && (d <= OVT_SAME_SPOT_M || (d <= OVT_MATCH_M && similarNames(p.name, l.description)))) {
              best = l;
              bestD = d;
            }
          }
          if (best) {
            if (!best.phone && p.phone) best.phone = p.phone;
            if (!best.website && p.website) best.website = p.website;
            if (!best.email && p.email) best.email = p.email;
            // OSM had no name, only the "Campsite (OSM 123)" fallback.
            if (!best._osm.tags.name && p.name) best.description = p.name;
            best._src = 'osm+overture';
            enriched.add(best._id);
            continue;
          }
          if (useOsm) continue; // only OSM places confirmed by Overture are listed
          if (added >= limit) {
            overLimit++;
            continue;
          }
          const row = mapElement(overtureElement(p));
          if (!row) continue;
          row._id = `ovt_${p.id}`;
          row._src = 'overture';
          const cat = pickedCats.find((c) => c.ovt.includes(p.category));
          if (cat && !forcePlaceTypeRef.current) row.placeType = cat.placeType;
          if (!p.name) row.description = `${row.placeType} (Overture)`;
          deduped.push(row);
          added++;
        }
        if (useOsm) {
          deduped = deduped.filter((l) => enriched.has(l._id));
          ovtNote = ` Overture ${release}: ${enriched.size} of ${osmCount} OSM place(s) confirmed — ${
            osmCount - enriched.size
          } not found in Overture were left out.`;
        } else {
          ovtNote =
            ` Overture ${release}: ${inArea.length} match(es).` +
            (overLimit ? ` ${overLimit} more left out by the result limit.` : '');
        }
      } catch (e: any) {
        const why = e?.message || String(e);
        // In OSM + Overture mode nothing can be confirmed without Overture,
        // so show nothing rather than unconfirmed OSM rows.
        setSearching(false);
        setStatus(
          `Overture ${useOsm ? 'check' : 'search'} failed: ${why}. Try again, or switch "Data sources" to OSM only.`,
          'err'
        );
        return;
      }
    }

    // Narrow by the selected services/activities. Matching runs against the
    // derived labels (which already searched every OSM field), so a place is
    // kept only if it has ALL picked services and ALL picked activities.
    // Overture carries no such data, so in Overture-only mode they can't apply.
    const pickedSvc = useOsm ? SERVICE_FACETS.filter((f) => svcSel[f.id]).map((f) => f.label) : [];
    const pickedAct = useOsm ? ACTIVITY_FACETS.filter((f) => actSel[f.id]).map((f) => f.label) : [];
    if (!useOsm && (selSvc.length || selAct.length))
      ovtNote += ' Services/activities filters ignored — Overture has no such data.';
    const next =
      pickedSvc.length || pickedAct.length
        ? deduped.filter(
            (l) => pickedSvc.every((s) => l.services.includes(s)) && pickedAct.every((a) => l.activities.includes(a))
          )
        : deduped;
    const filteredOut = deduped.length - next.length;
    // Stamp where the rows came from: the table filter and the saved draft
    // both go by this, and it is stripped again before export/upload.
    if (country) next.forEach((l) => (l._country = country));
    // Phones are tidied the moment they're fetched (spaces, dashes, "8…"
    // trunk prefix → "+370…"), so the table already shows what gets stored.
    next.forEach((l) => {
      l.phone = normalizePhone(l.phone, l._country || (l._osm?.tags?.['addr:country'] as string) || '');
    });

    // attach photos
    const key = (gmapsKeyRef.current?.value || '').trim();
    const uploadedBy = defUploadedByRef.current?.value || DEFAULT_UPLOADER;
    const verify = svVerifyRef.current?.checked || false;
    if (photoSrc !== 'none') {
      setStatus(`Found ${next.length} locations. Adding photos…`);
      await pool(next, 6, async (l) => {
        const urls = osmImageUrls(l._osm.tags);
        if (photoSrc === 'streetview' && key) {
          let ok = true;
          if (verify) ok = await svExists(l.latitude, l.longitude, key);
          if (ok) urls.push(svUrl(l.latitude, l.longitude, key));
        }
        if (photoSrc === 'satellite') {
          urls.push(satUrl(l.latitude, l.longitude, satMeters));
        }
        l.images = imagesMap(urls, uploadedBy);
      });
    }

    setSearching(false);
    setStatus(
      `Found ${next.length} locations` +
        (country ? ` in ${countryName(country)}` : '') +
        (filteredOut > 0 ? ` (${filteredOut} filtered out by service/activity).` : '.') +
        ovtNote,
      'ok'
    );
    setLocations(next);
    drawMarkers(next);

    // One reverse-geocode of the results' centroid → region hint for GPT.
    setRegion('');
    if (next.length) {
      const cLat = next.reduce((s, l) => s + l.latitude, 0) / next.length;
      const cLon = next.reduce((s, l) => s + l.longitude, 0) / next.length;
      reverseGeocodeRegion(cLat, cLon).then(setRegion);
    }
  }, [buildQuery, mapElement, drawMarkers, photoSrc, satMeters, cats, svcSel, actSel, country, dataSource, ovtMinConf]);

  // keep preview in sync with the working set / filters
  useEffect(() => {
    refreshPreview();
  }, [refreshPreview]);

  /* ---------- draft autosave (debounced) ---------- */
  // Every edit, AI fill, selection and upload flag lands back in localStorage,
  // so nothing that was not uploaded is lost when the browser closes.
  useEffect(() => {
    draftRef.current = {
      savedAt: Date.now(),
      locations,
      region,
      noteLabels,
      serviceOnlyResult,
      forcePlaceType: forcePlaceTypeRef.current,
      quickFilter,
      payFilter,
      country,
    };
    setDraftState(locations.length ? 'saving' : 'idle');
    const t = setTimeout(() => {
      const err = saveDraft(draftRef.current);
      setDraftErr(err);
      setDraftState(err ? 'error' : locations.length ? 'saved' : 'idle');
    }, 600);
    return () => clearTimeout(t);
  }, [locations, region, noteLabels, serviceOnlyResult, quickFilter, payFilter, country]);

  /* ---------- flush the pending save when the tab goes away ---------- */
  // The debounce above can still be waiting when the page is closed, so write
  // the latest snapshot synchronously on the way out. visibilitychange covers
  // mobile/background tabs, where beforeunload is not guaranteed to fire.
  useEffect(() => {
    const flush = () => {
      if (draftRef.current.locations.length || localStorage.getItem(DRAFT_KEY)) saveDraft(draftRef.current);
    };
    const onHide = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('beforeunload', flush);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('beforeunload', flush);
      document.removeEventListener('visibilitychange', onHide);
      flush();
    };
  }, []);

  /* ---------- restored draft: say so, and put the pins back on the map ---------- */
  useEffect(() => {
    const d = initialDraft;
    if (!d?.locations.length) return;
    const pending = d.locations.filter((l) => !l._uploaded).length;
    const when = new Date(d.savedAt || Date.now()).toLocaleString();
    setStatus(
      `Restored ${d.locations.length} location(s) from ${when} — ${pending} not uploaded yet. ` +
        'Keep editing them, or use "Clear results" to drop them.',
      'ok'
    );
    // Leaflet is loaded on demand, so the map may not exist yet — wait for it
    // before drawing the restored markers (give up after ~10s).
    let tries = 0;
    const t = setInterval(() => {
      if (mapRef.current && (window as any).L) {
        drawMarkers(d.locations);
        clearInterval(t);
      } else if (++tries > 100) {
        clearInterval(t);
      }
    }, 100);
    return () => clearInterval(t);
    // runs once on mount: the restored set never changes by itself
  }, []);

  // A row's phone is read against the country it was fetched under, then the
  // one selected now, then whatever OSM itself claims for the address.
  const phoneCountry = (l: PlaceItem) =>
    l._country || country || (l._osm?.tags?.['addr:country'] as string) || '';

  /* ---------- country limit ---------- */
  // Choosing a country limits the next fetch, hides rows from anywhere else,
  // and unticks them too — so "ticked = will be uploaded" stays true even for
  // rows the filter has hidden.
  const pickCountry = useCallback(async (code: string) => {
    setCountry(code);
    if (code) setLocations((prev) => prev.map((l) => (l._country === code ? l : { ...l, _sel: false })));
    if (!code) {
      setStatus('Country limit off — the search uses the map view alone again.', 'ok');
      return;
    }
    setStatus(`Limiting to ${countryName(code)} — centring the map…`);
    try {
      const bb = (await geoSearch(countryName(code), code))?.bbox; // [south, north, west, east]
      if (bb && mapRef.current) {
        mapRef.current.fitBounds([
          [bb[0], bb[2]],
          [bb[1], bb[3]],
        ]);
      }
      setStatus(
        `Search limited to ${countryName(code)}. Zoom in for a smaller area — the fetch is cut to the country either way.`,
        'ok'
      );
    } catch {
      setStatus(`Search limited to ${countryName(code)} (map not centred — pan there yourself).`, 'ok');
    }
  }, []);

  /* ---------- geocode ---------- */
  const geocode = useCallback(async () => {
    const q = (placeNameRef.current?.value || '').trim();
    if (!q) return;
    setStatus('Looking up “' + q + '”…');
    try {
      const hit = await geoSearch(q);
      if (!hit) {
        setStatus('Place not found.', 'err');
        return;
      }
      const bb = hit.bbox; // [south, north, west, east]
      mapRef.current?.fitBounds([
        [bb[0], bb[2]],
        [bb[1], bb[3]],
      ]);
      setStatus('Centred on “' + hit.name + '”. Now search.', 'ok');
    } catch (e: any) {
      setStatus('Geocoding failed: ' + e.message, 'err');
    }
  }, []);

  /* ---------- selection / row mutations ---------- */
  // What the services / activities checkboxes offer, rebuilt as rows arrive.
  const serviceOptions = useMemo(
    () => mergeOptions(SERVICE_OPTIONS, locations.flatMap((l) => l.services || [])),
    [locations]
  );
  const activityOptions = useMemo(
    () => mergeOptions(ACTIVITY_OPTIONS, locations.flatMap((l) => l.activities || [])),
    [locations]
  );

  const selCount = locations.filter((l) => l._sel).length;
  // Ticked AND not yet in the database — exactly what "Upload selected" pushes.
  const selPending = locations.filter((l) => l._sel && !l._uploaded);
  const uploadedCount = locations.filter((l) => l._uploaded).length;
  const hiddenByCountry = country ? locations.filter((l) => l._country !== country).length : 0;
  const pendingCount = locations.length - uploadedCount;
  const shown = useMemo(() => visibleRows(), [visibleRows]);
  const pg = usePage(shown.length, `${quickFilterDeferred}|${payFilter}|${country}`);
  const pageRows = shown.slice(pg.start, pg.end);
  const anyCatSelected =
    CATEGORIES.some((c) => cats[c.id]) ||
    SERVICE_FACETS.some((f) => svcSel[f.id]) ||
    ACTIVITY_FACETS.some((f) => actSel[f.id]);

  const toggleRow = (id: string, val: boolean) =>
    setLocations((prev) => prev.map((l) => (l._id === id ? { ...l, _sel: val } : l)));

  const deleteRow = (id: string) => {
    const map = mapRef.current;
    if (map && markersRef.current[id]) {
      map.removeLayer(markersRef.current[id]);
      delete markersRef.current[id];
    }
    setLocations((prev) => prev.filter((l) => l._id !== id));
  };

  const commitEdit = (id: string, field: string, value: string) => {
    setLocations((prev) =>
      prev.map((l) => {
        if (l._id !== id) return l;
        // Typed-in numbers are cleaned right away, so the table always shows
        // what the upload will actually store.
        const v =
          field === 'rating'
            ? Number(value) || 0
            : field === 'phone'
            ? normalizePhone(value, phoneCountry(l))
            : value;
        return { ...l, [field]: v };
      })
    );
    setEditing(null);
  };

  // services/activities are arrays — ticked in the CheckboxPicker list,
  // not the free-text EditableCell used for scalar fields.
  const updateChips = (id: string, field: 'services' | 'activities', next: string[]) => {
    setLocations((prev) => prev.map((l) => (l._id === id ? { ...l, [field]: next } : l)));
  };

  // Ticking the header box selects the visible rows that still need uploading
  // (already-uploaded ones are left alone); unticking clears every visible row.
  const headerCheck = (val: boolean) => {
    const ids = new Set(shown.map((l) => l._id));
    setLocations((prev) =>
      prev.map((l) => (ids.has(l._id) ? { ...l, _sel: val ? !l._uploaded : false } : l))
    );
  };

  const selectAll = (val: boolean) => setLocations((prev) => prev.map((l) => ({ ...l, _sel: val })));

  // Tick every row that has NOT been uploaded yet, so "select → upload" can
  // never re-send rows that are already in the database.
  const selectPending = () => setLocations((prev) => prev.map((l) => ({ ...l, _sel: !l._uploaded })));

  // After "Check duplicates": tick exactly the visible rows flagged as
  // duplicates (everything else unticked), ready for "Delete selected".
  const shownDups = shown.filter((l) => l._dupWarning && !l._uploaded);
  const selectDuplicates = () => {
    const ids = new Set(shownDups.map((l) => l._id));
    setLocations((prev) => prev.map((l) => ({ ...l, _sel: ids.has(l._id) })));
    setStatus(`Selected ${ids.size} duplicate(s) — press "Delete selected" to remove them from the table.`, 'ok');
  };

  // Same for rows the check flagged as having no (or a broken) photo.
  const shownPhotoIssues = shown.filter((l) => l._photoWarning && !l._uploaded);
  const selectPhotoIssues = () => {
    const ids = new Set(shownPhotoIssues.map((l) => l._id));
    setLocations((prev) => prev.map((l) => ({ ...l, _sel: ids.has(l._id) })));
    setStatus(`Selected ${ids.size} row(s) with no or broken photos.`, 'ok');
  };

  const deleteSelected = () => {
    const map = mapRef.current;
    locations.filter((l) => l._sel).forEach((l) => {
      if (map && markersRef.current[l._id]) {
        map.removeLayer(markersRef.current[l._id]);
        delete markersRef.current[l._id];
      }
    });
    setLocations((prev) => prev.filter((l) => !l._sel));
  };

  const bulkSet = () => {
    const f = bulkFieldRef.current?.value || 'placeType';
    const v = bulkValueRef.current?.value ?? '';
    const sel = locations.filter((l) => l._sel);
    if (!sel.length) {
      setStatus('Select some rows first (tick the checkbox on each row, or "Select all").', 'err');
      return;
    }
    if (!v.trim()) {
      setStatus('Type a value first.', 'err');
      return;
    }
    setLocations((prev) =>
      prev.map((l) => (l._sel ? { ...l, [f]: f === 'rating' ? Number(v) || 0 : v } : l))
    );
    setStatus(`Set ${f} on ${sel.length} rows.`, 'ok');
  };

  // Applies every ticked value at once to every ticked row.
  const arrEdit = (add: boolean) => {
    const f = arrField;
    const vals = arrPick;
    const sel = locations.filter((l) => l._sel);
    if (!sel.length) {
      setStatus('Select some rows first (tick the checkbox on each row, or "Select all").', 'err');
      return;
    }
    if (!vals.length) {
      setStatus(`Tick at least one ${f} value in the list first.`, 'err');
      return;
    }
    setLocations((prev) =>
      prev.map((l) => {
        if (!l._sel) return l;
        const arr: string[] = Array.isArray(l[f]) ? l[f] : [];
        return {
          ...l,
          [f]: add
            ? Array.from(new Set([...arr, ...vals]))
            : arr.filter((x: string) => !vals.includes(x)),
        };
      })
    );
    setStatus(
      `${add ? 'Added' : 'Removed'} ${vals.join(', ')} ${add ? 'to' : 'from'} ${f} on ${sel.length} rows.`,
      'ok'
    );
  };

  // The editable prompt sets: one per place type.
  const PROMPT_SET_KEYS = [...PLACETYPE_ENUM];

  // Which prompt to actually send for a given (place type, field): the place
  // type's own edit, or undefined (generateField then uses the built-in
  // FIELD_PROMPTS default).
  const effectivePrompt = (placeType: string, field: string): string | undefined =>
    promptSets[placeType]?.[field];

  // What the editor shows for the selected set/field: the edit, or the built-in
  // default text.
  const shownPrompt = (setKey: string, field: string): string =>
    promptSets[setKey]?.[field] ?? FIELD_PROMPTS[field];

  const setPrompt = (setKey: string, field: string, value: string) =>
    setPromptSets((prev) => ({ ...prev, [setKey]: { ...(prev[setKey] || {}), [field]: value } }));

  const clearPromptField = (setKey: string, field: string) =>
    setPromptSets((prev) => {
      const set = { ...(prev[setKey] || {}) };
      delete set[field];
      const next = { ...prev };
      if (Object.keys(set).length) next[setKey] = set;
      else delete next[setKey];
      return next;
    });

  const clearPromptSet = (setKey: string) =>
    setPromptSets((prev) => {
      const next = { ...prev };
      delete next[setKey];
      return next;
    });

  // Service / activity extra-note editing (single instruction each).
  const SERVICE_LABELS = SERVICE_FACETS.map((f) => f.label);
  const ACTIVITY_LABELS = ACTIVITY_FACETS.map((f) => f.label);
  const setFacetHint = (label: string, value: string) =>
    setFacetHints((prev) => {
      const next = { ...prev };
      if (value.trim()) next[label] = value;
      else delete next[label];
      return next;
    });

  // Effective description instruction for one location: its place-type set's
  // description prompt (or the built-in default), with the notes for the
  // governing search filter (noteLabels) appended. The governing parent
  // (Places > Services > Activities) is decided in search().
  const descriptionInstrFor = (placeType: string): string | undefined => {
    const base = effectivePrompt(placeType, 'description');
    const notes: string[] = [];
    for (const k of noteLabels) {
      const n = facetHints[k]?.trim();
      if (n && !notes.includes(n)) notes.push(n);
    }
    if (!notes.length) return base;
    return (base ?? FIELD_PROMPTS.description) + ' Also, for this location specifically: ' + notes.join(' ');
  };

  // GPT-generate the chosen field for ALL extracted locations, replacing the
  // existing value. Array fields (services/activities) are split from a list.
  const ARRAY_FIELDS = ['services', 'activities'];
  // Contact fields only make sense for campsites/caravan/motorhome places and
  // service points (laundrette, gas/LPG, vehicle wash) — see wantsContactInfo.
  const CONTACT_FIELDS = ['phone', 'website', 'email'];
  const generateForAll = async () => {
    if (!locations.length) {
      setStatus('No locations yet — run a search first.', 'err');
      return;
    }
    if (!hasOpenAIKey()) {
      setStatus('AI backend not reachable — is server.js running (and /api proxied to it on the VPS)?', 'err');
      return;
    }
    const field = gptField;
    if (serviceOnlyResult && field === 'placeType') {
      setStatus('Place type stays "Services" for a services-only search — not regenerated.', 'ok');
      return;
    }
    const total = locations.length;
    setGenerating(true);
    const results: Record<string, string | string[]> = {};
    let done = 0;
    let failed = 0;
    let skipped = 0;
    let lastErr = '';
    await pool(locations, 3, async (l) => {
      if (CONTACT_FIELDS.includes(field)) {
        // Never overwrite a contact value we already have (e.g. pulled straight
        // from OSM tags) — AI only fills in what's genuinely missing.
        if (l[field]) {
          skipped++;
          setStatus(`Generating ${field}… ${done + failed + skipped}/${total}`);
          return;
        }
        if (!wantsContactInfo(l.placeType, l.services)) {
          skipped++;
          setStatus(`Generating ${field}… ${done + failed + skipped}/${total}`);
          return;
        }
      }
      try {
        const raw = await generateField(
          field,
          {
            name: l._osm?.tags?.name || l.description,
            type: l.placeType,
            region: region || undefined,
            services: l.services,
            activities: l.activities,
            payment: l.paymentStatus,
            latitude: l.latitude,
            longitude: l.longitude,
          },
          field === 'description'
            ? descriptionInstrFor(l.placeType)
            : effectivePrompt(l.placeType, field)
        );
        if (raw) {
          results[l._id] = ARRAY_FIELDS.includes(field)
            ? parseList(raw)
            : field === 'phone'
            ? normalizePhone(raw, phoneCountry(l))
            : raw;
        }
        done++;
      } catch (e: any) {
        failed++;
        lastErr = e?.message || String(e);
      }
      setStatus(`Generating ${field}… ${done + failed + skipped}/${total}`);
    });
    setLocations((prev) => prev.map((l) => (l._id in results ? { ...l, [field]: results[l._id] } : l)));
    setGenerating(false);
    setStatus(
      `Generated ${field} for ${done} location(s)` +
        (skipped ? `, ${skipped} skipped (already set, or not a campsite/motorhome/service place)` : '') +
        (failed ? `, ${failed} failed — ${lastErr}` : '') +
        '.',
      failed ? 'err' : 'ok'
    );
  };

  // Build the fact bundle sent to GPT for one location.
  const factsFor = (l: PlaceItem) => ({
    name: l._osm?.tags?.name || l.description,
    type: l.placeType,
    region: region || undefined,
    services: l.services,
    activities: l.activities,
    payment: l.paymentStatus,
    latitude: l.latitude,
    longitude: l.longitude,
  });

  // GPT-generate EVERY field for ALL locations in one pass. Empty results keep
  // the existing value (so it never wipes good OSM data with a blank).
  const ALL_GPT_FIELDS = ['placeType', 'description', 'paymentStatus', 'services', 'activities', 'heightLimit'];
  const generateAllFields = async () => {
    if (!locations.length) {
      setStatus('No locations yet — run a search first.', 'err');
      return;
    }
    if (!hasOpenAIKey()) {
      setStatus('AI backend not reachable — is server.js running (and /api proxied to it on the VPS)?', 'err');
      return;
    }
    const total = locations.length;
    // Keep placeType as "Services" for a services-only search (don't regenerate).
    const fieldsToGen = serviceOnlyResult ? ALL_GPT_FIELDS.filter((f) => f !== 'placeType') : ALL_GPT_FIELDS;
    setGenerating(true);
    const patches: Record<string, Partial<PlaceItem>> = {};
    let done = 0;
    let failed = 0;
    let lastErr = '';
    await pool(locations, 3, async (l) => {
      const facts = factsFor(l);
      const patch: Partial<PlaceItem> = {};
      try {
        // Phase 1: generate the data fields first (all but description).
        await Promise.all(
          fieldsToGen
            .filter((field) => field !== 'description')
            .map(async (field) => {
              const raw = await generateField(field, facts, effectivePrompt(l.placeType, field));
              if (ARRAY_FIELDS.includes(field)) {
                const arr = parseList(raw);
                if (arr.length) (patch as any)[field] = arr;
              } else if (raw) {
                (patch as any)[field] = raw;
              }
            })
        );
        // Phase 2: write the description from the UPDATED facts, so it can't
        // contradict the payment / services / type just generated above.
        const effType = (patch.placeType as string) || l.placeType;
        const effServices = (patch.services as string[]) || l.services;
        const effActivities = (patch.activities as string[]) || l.activities;
        if (fieldsToGen.includes('description')) {
          const raw = await generateField(
            'description',
            {
              ...facts,
              type: effType,
              payment: patch.paymentStatus || facts.payment,
              services: effServices,
              activities: effActivities,
            },
            descriptionInstrFor(effType)
          );
          if (raw) patch.description = raw;
        }
        // Phase 3: contact info (phone/website/email) — only for place types
        // or services where this is actually meaningful (campsites, caravan
        // and motorhome places, laundrette, gas/LPG, vehicle wash), and only
        // to fill in what's missing — never overwrite a value already pulled
        // from OSM tags.
        if (wantsContactInfo(effType, effServices)) {
          await Promise.all(
            CONTACT_FIELDS.filter((field) => !l[field]).map(async (field) => {
              const raw = await generateField(
                field,
                { ...facts, type: effType, services: effServices },
                effectivePrompt(effType, field)
              );
              if (raw) (patch as any)[field] = field === 'phone' ? normalizePhone(raw, phoneCountry(l)) : raw;
            })
          );
        }
        patches[l._id] = patch;
        done++;
      } catch (e: any) {
        failed++;
        lastErr = e?.message || String(e);
      }
      setStatus(`Generating all fields… ${done + failed}/${total}`);
    });
    setLocations((prev) => prev.map((l) => (patches[l._id] ? { ...l, ...patches[l._id] } : l)));
    setGenerating(false);
    setStatus(
      `Generated all fields for ${done} location(s)${failed ? `, ${failed} failed — ${lastErr}` : ''}.`,
      failed ? 'err' : 'ok'
    );
  };

  const toggleMode = () => {
    setMode((m) => {
      const next = m === 'demo' ? 'production' : 'demo';
      localStorage.setItem('cl_admin_mode', next);
      return next;
    });
  };

  // Upload ONLY the ticked rows straight into the live database:
  // places/{pushId} plus its places_locations/{pushId} geo twin. Rows left
  // unticked stay in the table and can keep being edited and uploaded later;
  // rows already uploaded once are skipped so re-clicking can't duplicate.
  const uploadToFirebase = async () => {
    if (mode !== 'production') {
      setStatus('Demo mode — switch to PRODUCTION (top right) to upload.', 'err');
      return;
    }
    const rows = locations.filter((l) => l._sel && !l._uploaded);
    if (!rows.length) {
      setStatus(
        locations.some((l) => l._sel)
          ? 'Every selected row is already uploaded — tick other rows to upload them.'
          : 'Nothing selected — tick the checkbox on the rows you want to upload (or use "Select not uploaded").',
        'err'
      );
      return;
    }
    const who = defAddedByRef.current?.value || DEFAULT_UPLOADER;
    if (!window.confirm(`Upload ${rows.length} location(s) to the LIVE database as user "${who}"?`)) return;
    setUploading(true);

    // Duplicate check runs against EVERY existing location in the live
    // database (all users, not just `who`).
    setStatus('Checking for duplicate locations…');
    let existingPoints: GeoPoint[];
    let geoFullScan = false;
    try {
      ({ points: existingPoints, fullScan: geoFullScan } = await loadExistingPointsNear(rows));
    } catch (e: any) {
      setUploading(false);
      setStatus(`Duplicate check failed (${e?.message || e}) — upload aborted so nothing is uploaded unchecked.`, 'err');
      return;
    }

    let done = 0;
    let failed = 0;
    let photoFails = 0;
    let duplicates = 0;
    let phonesFixed = 0;
    const photoErrs: string[] = [];
    let lastErr = '';
    const dupNames: string[] = [];
    await pool(rows, 4, async (l) => {
      try {
        const nearest = nearestDistanceMeters(l.latitude, l.longitude, existingPoints);
        if (nearest <= DUPLICATE_RADIUS_M) {
          duplicates++;
          const label = (l.description || l._id).slice(0, 60);
          dupNames.push(`${label} (${Math.round(nearest)}m away)`);
          setLocations((prev) =>
            prev.map((x) =>
              x._id === l._id
                ? { ...x, _dupWarning: `Duplicate — a location already exists ${Math.round(nearest)}m away` }
                : x
            )
          );
          setStatus(`Uploading to Firebase… ${done + failed + duplicates}/${rows.length}`);
          return;
        }
        const { _id, _sel, _osm, _uploaded, _dupWarning, _photoWarning, _country, _src, ...clean } = l as any;
        // Fill the fields the mobile app expects on every place.
        clean.addedBy = who;
        clean.heightLimit = clean.heightLimit || 'None';
        clean.placesAvailable = clean.placesAvailable || 'None';
        clean.email = clean.email || '';
        // One shape in the database, whatever OSM or the editor produced.
        const cleanedPhone = normalizePhone(clean.phone || '', phoneCountry(l));
        if (cleanedPhone !== (clean.phone || '')) phonesFixed++;
        clean.phone = cleanedPhone;
        clean.website = clean.website || '';
        clean.verified = clean.verified ?? true;
        // The record's dates are the moment it enters the database — never
        // the fetch date, and never a stale value carried in a draft.
        const now = fmtDateSlash(new Date());
        clean.dateAdded = now;
        clean.lastUpdatedCostsDate = now;
        const id = pushId();
        // Re-host every photo in OUR Firebase Storage (places/{id}/image_N.jpg).
        // The mobile app resolves photo URLs back to Storage refs (e.g. when
        // deleting a place), which crashes on external URLs — so external
        // satellite/OSM images must be copied in, not linked.
        const images: Record<string, ImageEntry> = {};
        let imgFailed = 0;
        let idx = 0;
        for (const [, img] of orderedImages(l.images)) {
          try {
            const blob = await fetchImageBlobWithRetry(img.url);
            const problem = await imageProblem(blob);
            if (problem) throw new Error(problem);
            const key = `image_${idx}`;
            const objRef = sRef(storage, `places/${id}/${key}.jpg`);
            await uploadBytes(objRef, blob, { contentType: blob.type || 'image/jpeg' });
            images[key] = {
              dateUploaded: fmtDateSlash(new Date()),
              uploadedBy: img.uploadedBy || who,
              url: await getDownloadURL(objRef),
            };
            idx++;
          } catch (e: any) {
            imgFailed++; // skip this photo, keep the location — retried already, this is a hard failure
            photoErrs.push(`"${(clean.description || id).slice(0, 40)}" — ${e?.message || e}`);
            console.warn(`[upload] photo rejected for "${clean.description || id}": ${img.url} — ${e?.message || e}`);
          }
        }
        photoFails += imgFailed;
        clean.images = images;
        await update(ref(db), {
          [`places/${id}`]: clean,
          [`places_locations/${id}`]: { g: geohash(l.latitude, l.longitude), l: [l.latitude, l.longitude] },
        });
        // So a duplicate later in this same batch also gets caught.
        existingPoints.push({ lat: l.latitude, lon: l.longitude });
        // Untick it too: what stays selected is always "still to upload".
        setLocations((prev) =>
          prev.map((x) =>
            x._id === l._id ? { ...x, _uploaded: true, _sel: false, _dupWarning: undefined } : x
          )
        );
        done++;
      } catch (e: any) {
        failed++;
        lastErr = e?.message || String(e);
      }
      setStatus(`Uploading to Firebase… ${done + failed + duplicates}/${rows.length}`);
    });
    setUploading(false);
    const photoNote = photoFails
      ? ` ${photoFails} photo(s) rejected as unusable and skipped: ${photoErrs.slice(0, 2).join('; ')}${
          photoErrs.length > 2 ? `, +${photoErrs.length - 2} more (see the browser console)` : ''
        }.`
      : '';
    const phoneNote = phonesFixed ? ` ${phonesFixed} phone number(s) were tidied into +country format.` : '';
    const dupNote = duplicates
      ? ` Skipped ${duplicates} likely duplicate(s): ${dupNames.slice(0, 3).join('; ')}${
          dupNames.length > 3 ? `, +${dupNames.length - 3} more` : ''
        }.`
      : '';
    const left = locations.length - uploadedCount - done;
    const leftNote = left > 0 ? ` ${left} location(s) stay in the table and can still be edited.` : '';
    setStatus(
      failed
        ? `Uploaded ${done}, FAILED ${failed} (${lastErr}).${photoNote}${phoneNote}${dupNote}${leftNote} Check your database rules / connection.`
        : `Uploaded ${done} location(s) as "${who}".${photoNote}${phoneNote}${dupNote}${leftNote} See them in the "Manage uploads" tab.` +
          (geoFullScan ? NO_GEO_INDEX_NOTE : ''),
      failed || photoFails || duplicates ? 'err' : 'ok'
    );
  };

  // Dry run of the upload's checks, without writing anything: flags rows that
  // are already in Firebase — within DUPLICATE_RADIUS_M of a live location,
  // which also catches rows uploaded in an earlier session — or of another row
  // in this check, and photos that fail to download or decode. Runs on the
  // ticked not-uploaded rows, or on every not-uploaded row when none is ticked.
  const checkRows = async () => {
    const ticked = locations.filter((l) => l._sel && !l._uploaded);
    const rows = ticked.length ? ticked : locations.filter((l) => !l._uploaded);
    if (!rows.length) {
      setStatus('Nothing to check — every row is already uploaded.', 'err');
      return;
    }
    setChecking(true);
    setStatus('Checking which locations are already in Firebase…');
    let existingPoints: GeoPoint[];
    let geoFullScan = false;
    try {
      ({ points: existingPoints, fullScan: geoFullScan } = await loadExistingPointsNear(rows));
    } catch (e: any) {
      setChecking(false);
      setStatus(`Duplicate check failed (${e?.message || e}).`, 'err');
      return;
    }

    // Rows are compared against each other too, in table order, so the later
    // of two near-identical results is the one flagged.
    const dupWarn: Record<string, string | undefined> = {};
    for (const l of rows) {
      const nearestLive = nearestDistanceMeters(l.latitude, l.longitude, existingPoints);
      if (nearestLive <= DUPLICATE_RADIUS_M) {
        dupWarn[l._id] = `Duplicate — a location already exists ${Math.round(nearestLive)}m away`;
        continue;
      }
      const earlier = rows.slice(0, rows.indexOf(l)).filter((r) => !dupWarn[r._id]);
      const twin = earlier.find(
        (r) => haversineMeters(l.latitude, l.longitude, r.latitude, r.longitude) <= DUPLICATE_RADIUS_M
      );
      dupWarn[l._id] = twin
        ? `Duplicate of "${(twin.description || twin._id).slice(0, 40)}" in this table (${Math.round(
            haversineMeters(l.latitude, l.longitude, twin.latitude, twin.longitude)
          )}m away)`
        : undefined;
    }
    const duplicates = Object.values(dupWarn).filter(Boolean).length;
    setLocations((prev) => prev.map((x) => (x._id in dupWarn ? { ...x, _dupWarning: dupWarn[x._id] } : x)));

    let checked = 0;
    let badPhotos = 0;
    let rowsWithBad = 0;
    let noPhoto = 0;
    await pool(rows, 4, async (l) => {
      const imgs = Object.values((l.images || {}) as Record<string, ImageEntry>);
      const problems: string[] = [];
      if (!imgs.length) {
        noPhoto++;
        problems.push('no photo');
      }
      for (const [i, img] of imgs.entries()) {
        try {
          const blob = await fetchImageBlobWithRetry(img.url);
          const problem = await imageProblem(blob);
          if (problem) throw new Error(problem);
        } catch (e: any) {
          badPhotos++;
          problems.push(`photo ${i + 1}: ${e?.message || e}`);
          console.warn(`[check] photo problem for "${l.description || l._id}": ${img.url} — ${e?.message || e}`);
        }
      }
      if (problems.length && imgs.length) rowsWithBad++;
      const warning = problems.length ? problems.join('; ') : undefined;
      setLocations((prev) => prev.map((x) => (x._id === l._id ? { ...x, _photoWarning: warning } : x)));
      checked++;
      setStatus(`Checking photos… ${checked}/${rows.length}`);
    });
    setChecking(false);
    const scope = ticked.length ? `${rows.length} selected` : `all ${rows.length} not-uploaded`;
    const parts = [
      duplicates ? `${duplicates} likely duplicate(s)` : '',
      badPhotos ? `${badPhotos} missing/corrupt photo(s) in ${rowsWithBad} row(s)` : '',
      noPhoto ? `${noPhoto} row(s) with no photo at all` : '',
    ].filter(Boolean);
    setStatus(
      parts.length
        ? `Checked ${scope} row(s): ${parts.join(', ')} — see the red notes in the table.`
        : `Checked ${scope} row(s): none already in Firebase, every photo downloads and decodes.` +
          (geoFullScan ? NO_GEO_INDEX_NOTE : ''),
      parts.length ? 'err' : 'ok'
    );
  };

  // Wipe the current results (table, map markers, preview) for a fresh query.
  // Warns when rows that were never uploaded would be discarded.
  const clearResults = () => {
    const notUploaded = locations.filter((l) => !l._uploaded).length;
    if (
      notUploaded > 0 &&
      !window.confirm(`${notUploaded} location(s) were NOT uploaded — discard them anyway?`)
    )
      return;
    const map = mapRef.current;
    if (map) Object.values(markersRef.current).forEach((m: any) => map.removeLayer(m));
    markersRef.current = {};
    setLocations([]);
    setRegion('');
    setNoteLabels([]);
    setServiceOnlyResult(false);
    forcePlaceTypeRef.current = null;
    setQuickFilter('');
    setPayFilter('');
    // Drop the stored copy immediately, not just on the next autosave tick.
    draftRef.current = {
      savedAt: Date.now(),
      locations: [],
      region: '',
      noteLabels: [],
      serviceOnlyResult: false,
      forcePlaceType: null,
      quickFilter: '',
      payFilter: '',
      country, // a search setting, not a result — it survives clearing
    };
    saveDraft(draftRef.current);
    setDraftState('idle');
    setStatus('Results cleared (here and in this browser\'s saved copy) — run a new search.', 'ok');
  };

  const copyJson = async () => {
    const text = JSON.stringify(toExport(), null, 2);
    try {
      await navigator.clipboard.writeText(text);
      setStatus('Copied JSON to clipboard.', 'ok');
    } catch {
      setPreview(text); // the full file, to select by hand
      setStatus('Copy failed — select the text manually.', 'err');
    }
  };

  const downloadJson = () => {
    const text = JSON.stringify(toExport(), null, 2);
    const blob = new Blob([text], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `camplocation-osm-export-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/admin/login', { replace: true });
  };

  // Open the photo browser at the first image of a location.
  const openLightbox = (l: PlaceItem) => {
    if (!imageCount(l)) return;
    setLightboxId(l._id);
  };

  // Reorder a not-yet-uploaded row's photos. The upload stores them as
  // image_0, image_1… in this order, so the first one becomes the cover.
  const reorderLocalPhotos = (id: string, keys: string[]) => {
    setLocations((prev) =>
      prev.map((l) => (l._id === id ? { ...l, images: renumberImages(l.images || {}, keys) } : l))
    );
  };

  // Preview the satellite image for the current map centre at the chosen area.
  const previewSatellite = () => {
    const map = mapRef.current;
    const c = map ? map.getCenter() : { lat: 55.3, lng: 23.9 };
    setSatPreview(satUrl(c.lat, c.lng, satMeters) + '&_t=' + Date.now());
  };

  const exportCount = useMemo(
    () => `exporting ${shown.length} of ${locations.length}`,
    [shown.length, locations.length]
  );

  /* ============================================================
     Render
     ============================================================ */
  return (
    <div className="osm-tool">
      <header className="osm-header">
        <img
          src="https://camplocation.com/logo_%20zoomed.png"
          alt="Camplocation"
          onClick={() => setView('import')}
          style={{ cursor: 'pointer' }}
          title="Back to OSM import"
          onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
        />
        <div>
          <h1>Camplocation — OSM Import Tool</h1>
          <div className="sub">Export locations from OpenStreetMap into your app</div>
        </div>
        <nav className="header-tabs">
          <button className={'tab' + (view === 'import' ? ' active' : '')} onClick={() => setView('import')}>
            OSM import
          </button>
          <button className={'tab' + (view === 'manage' ? ' active' : '')} onClick={() => setView('manage')}>
            Manage uploads
          </button>
          <button className={'tab' + (view === 'users' ? ' active' : '')} onClick={() => setView('users')}>
            Users
          </button>
        </nav>
        <button
          className={'modeswitch ' + mode}
          onClick={toggleMode}
          title="Click to switch. Demo: nothing is written to Firebase. Production: uploads, edits and deletes hit the live database."
        >
          {mode === 'production' ? '● PRODUCTION · writes to live Firebase' : '○ DEMO · display only · nothing is stored'}
        </button>
        <button className="signout" onClick={handleSignOut}>
          Sign out
        </button>
      </header>
      <div className="accentbar" />

      {view === 'manage' && <ManageView mode={mode} />}
      {view === 'users' && <UsersView mode={mode} />}

      <div className="wrap" style={view !== 'import' ? { display: 'none' } : undefined}>
        {/* ============ SIDEBAR : FILTERS ============ */}
        <aside className="side">
          <h2>1 · Search area</h2>
          <div className="card">
            <label>Country (limits the OSM / Overture fetch and the results table)</label>
            <select value={country} onChange={(e) => pickCountry(e.target.value)}>
              <option value="">Any country — map view only</option>
              <optgroup label="Nearby">
                {COUNTRIES.filter((c) => NEARBY_CODES.includes(c.code)).map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Europe">
                {COUNTRIES.filter((c) => !NEARBY_CODES.includes(c.code)).map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </optgroup>
            </select>
            <div className="hint">
              With a country picked, Overpass is asked for that country's area only, so a map view spilling over the
              border brings back nothing from the neighbours — and the table hides rows fetched under any other
              country.
            </div>
            <label style={{ marginTop: 10 }}>Find a place (city, region, country)</label>
            <div className="row">
              <input
                type="text"
                ref={placeNameRef}
                placeholder="e.g. Curonian Spit, Lithuania"
                style={{ flex: 2 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') geocode();
                }}
              />
              <button className="sm" style={{ flex: 1 }} onClick={geocode}>
                Find
              </button>
            </div>
            <div className="osm-map" ref={mapDivRef} style={{ marginTop: 10 }} />
            <button
              className="sm"
              style={{ width: '100%', marginTop: 8 }}
              onClick={() => setStatus('Search area set to current map view.', 'ok')}
            >
              📍 Use current map view as search area
            </button>
            <div className="hint">
              Search uses the visible map bounds. Zoom/pan to the area you want, then search.
            </div>
          </div>

          <h2>2 · What to import</h2>
          <div className="card">
            <label>By places (drives the fetch when picked)</label>
            <div className="cats">
              {CATEGORIES.map((c) => (
                <label className="chk" key={c.id}>
                  <input
                    type="checkbox"
                    checked={!!cats[c.id]}
                    onChange={(e) => setCats((prev) => ({ ...prev, [c.id]: e.target.checked }))}
                  />{' '}
                  {c.label}
                </label>
              ))}
            </div>

            <label style={{ marginTop: 10 }}>By services</label>
            <div className="cats">
              {SERVICE_FACETS.map((f) => (
                <label className="chk" key={f.id}>
                  <input
                    type="checkbox"
                    checked={!!svcSel[f.id]}
                    onChange={(e) => setSvcSel((prev) => ({ ...prev, [f.id]: e.target.checked }))}
                  />{' '}
                  {f.label}
                </label>
              ))}
            </div>

            <label style={{ marginTop: 10 }}>By activities</label>
            <div className="cats">
              {ACTIVITY_FACETS.map((f) => (
                <label className="chk" key={f.id}>
                  <input
                    type="checkbox"
                    checked={!!actSel[f.id]}
                    onChange={(e) => setActSel((prev) => ({ ...prev, [f.id]: e.target.checked }))}
                  />{' '}
                  {f.label}
                </label>
              ))}
            </div>
            <div className="hint">
              Tick any combination from any group. If you pick places, they drive the fetch and the services/activities
              narrow it (e.g. campsites <i>with</i> showers). If you pick no place, the chosen services/activities drive
              the search on their own. Matching runs across <b>every</b> OSM tag, and a place is kept only if it has all
              the services/activities you tick.
            </div>

            <label>Payment (filtered in the OSM request)</label>
            <select ref={osmFeeRef} defaultValue="">
              <option value="">Any payment</option>
              <option value="yes">Paid only (fee=yes)</option>
              <option value="no">Free only (fee=no)</option>
            </select>
            <label>Max results</label>
            <input type="number" ref={limitRef} defaultValue={300} min={1} max={2000} />
            <button
              className="primary"
              style={{ width: '100%', marginTop: 12 }}
              disabled={searching || !anyCatSelected}
              onClick={search}
            >
              🔍 Search OpenStreetMap
            </button>
            <div className={'status ' + status.cls} style={{ marginTop: 8 }}>
              {status.msg}
            </div>
          </div>

          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            3 · Import defaults
            <span className="hint" style={{ margin: 0, fontWeight: 'normal' }}>
              {importDefaultsSaved === 'saving'
                ? 'Saving…'
                : importDefaultsSaved === 'saved'
                ? '✓ Saved to your account'
                : importDefaultsSaved === 'error'
                ? '⚠ Save failed'
                : user
                ? 'Saved to your account'
                : 'Sign in to save'}
            </span>
          </h2>
          <div className="card">
            <label>Data sources</label>
            <select value={dataSource} onChange={(e) => setDataSource(e.target.value as DataSource)}>
              <option value="both">OSM + Overture (OSM places confirmed by Overture)</option>
              <option value="osm">OSM only</option>
              <option value="overture">Overture only</option>
            </select>
            {dataSource !== 'osm' && (
              <>
                <label>Overture minimum confidence ({Math.round(ovtMinConf * 100)}%)</label>
                <input
                  type="range"
                  min={0}
                  max={0.95}
                  step={0.05}
                  value={ovtMinConf}
                  onChange={(e) => setOvtMinConf(Number(e.target.value))}
                />
                <div className="hint">
                  {dataSource === 'both'
                    ? 'Only OSM places that Overture also has are listed (their missing phone / website / e-mail is filled from Overture). Overture-only places are not added. '
                    : 'Overture has no services, activities or fee data, so those filters and the free/paid motorhome, private parking and 4x4 types do not apply. '}
                  Lower confidence finds more places but more of them may no longer exist. The first Overture search in an
                  area takes 10–20 s.
                </div>
              </>
            )}
            <label>AI model (for the ✨ Generate buttons)</label>
            <select value={aiModel} onChange={(e) => setAiModel(e.target.value)}>
              {AI_MODELS.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label} · ~${(estimateCostPerLocation(m.id) * 100).toFixed(2)} / 100 locations
                  {m.id === DEFAULT_AI_MODEL ? ' (default)' : ''}
                  {m.retires ? ` · retires ${m.retires}` : ''}
                </option>
              ))}
            </select>
            <details className="ai-costs">
              <summary>Model prices and average cost</summary>
              <table>
                <thead>
                  <tr>
                    <th>model</th>
                    <th title="USD per 1M input / output tokens">$ / 1M in · out</th>
                    <th title="Estimated cost of “Generate ALL fields” for 100 locations">est. / 100 loc.</th>
                    <th title="Measured in this browser from the tokens OpenAI reported">avg / call (calls)</th>
                  </tr>
                </thead>
                <tbody>
                  {AI_MODELS.map((m) => {
                    const u = aiUsage[m.id];
                    return (
                      <tr key={m.id} className={m.id === aiModel ? 'on' : ''}>
                        <td title={m.retires ? `OpenAI shuts this model down on ${m.retires}` : undefined}>
                          {m.label}
                          {m.retires ? ' ⚠' : ''}
                        </td>
                        <td>
                          {m.inPrice} · {m.outPrice}
                        </td>
                        <td>${(estimateCostPerLocation(m.id) * 100).toFixed(2)}</td>
                        <td>{u ? `$${(u.usd / u.calls).toFixed(5)} (${u.calls})` : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="hint">
                The estimate is for “Generate ALL fields” (6 calls per location; campsites and service places add 3
                more for phone / website / e-mail). The average is measured from real calls in this browser.{' '}
                <button
                  className="sm"
                  type="button"
                  onClick={() => {
                    resetAIUsage();
                    setAiUsage(getAIUsage());
                  }}
                >
                  Reset averages
                </button>
              </div>
            </details>
            <label>addedBy (the user new locations are uploaded as)</label>
            <input
              type="text"
              ref={defAddedByRef}
              defaultValue={DEFAULT_UPLOADER}
              onChange={scheduleSaveImportDefaults}
            />
            <label>Default rating</label>
            <input
              type="number"
              ref={defRatingRef}
              defaultValue={0}
              min={0}
              max={5}
              onChange={scheduleSaveImportDefaults}
            />
            <label>Default activities (OSM rarely has these — added to every location, comma-separated)</label>
            <input
              type="text"
              ref={defActivitiesRef}
              placeholder="e.g. Hiking, Swimming"
              onChange={scheduleSaveImportDefaults}
            />
            <label>Default services (comma-separated)</label>
            <input
              type="text"
              ref={defServicesRef}
              placeholder="e.g. Toilets, Drinking water"
              onChange={scheduleSaveImportDefaults}
            />
            <label>Photos</label>
            <select value={photoSrc} onChange={(e) => setPhotoSrc(e.target.value)}>
              <option value="satellite">Satellite imagery (free, no key) + OSM photos</option>
              <option value="streetview">Google Street View + OSM photos</option>
              <option value="osm">OSM photo tags only (free)</option>
              <option value="none">None</option>
            </select>

            {photoSrc === 'satellite' && (
              <>
                <label>Satellite area (metres across)</label>
                <div className="row">
                  <input
                    type="range"
                    min={80}
                    max={1000}
                    step={20}
                    value={satMeters}
                    onChange={(e) => setSatMeters(Number(e.target.value))}
                    style={{ flex: 3 }}
                  />
                  <input
                    type="number"
                    min={50}
                    max={2000}
                    value={satMeters}
                    onChange={(e) => setSatMeters(Number(e.target.value) || 200)}
                    style={{ flex: 1 }}
                  />
                </div>
                <button className="sm" style={{ width: '100%', marginTop: 8 }} onClick={previewSatellite}>
                  🛰 Preview satellite (map centre)
                </button>
                {satPreview && (
                  <img
                    src={satPreview}
                    alt="Satellite preview"
                    style={{
                      width: '100%',
                      marginTop: 8,
                      borderRadius: 8,
                      border: '1px solid var(--line)',
                      display: 'block',
                    }}
                    onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                  />
                )}
                <div className="hint">
                  Top-down aerial view from Esri World Imagery. Pan the map above to a location, then preview.
                </div>
              </>
            )}

            {/* Kept mounted (just hidden) rather than conditionally rendered so the
                saved gmapsKey/svVerify values can always be restored onto these
                refs on load, even while Satellite is the active photo source. */}
            <div style={{ display: photoSrc === 'streetview' ? 'block' : 'none' }}>
              <label>Google Maps API key (for Street View)</label>
              <input type="text" ref={gmapsKeyRef} placeholder="AIza…" onChange={scheduleSaveImportDefaults} />
              <label className="chk" style={{ marginTop: 8 }}>
                <input type="checkbox" ref={svVerifyRef} onChange={scheduleSaveImportDefaults} /> Only add Street
                View where imagery exists (slower)
              </label>
            </div>
            <label>uploadedBy (user ID on photos)</label>
            <input
              type="text"
              ref={defUploadedByRef}
              defaultValue={DEFAULT_UPLOADER}
              onChange={scheduleSaveImportDefaults}
            />
            <label>Key format for export</label>
            <select
              ref={keyModeRef}
              defaultValue="push"
              onChange={() => {
                refreshPreview();
                scheduleSaveImportDefaults();
              }}
            >
              <option value="push">Firebase push ID (-Oxxxx…)</option>
              <option value="osm">OSM id (osm_node_123)</option>
            </select>
            <label>Wrap export under "places"</label>
            <select
              ref={wrapModeRef}
              defaultValue="places"
              onChange={() => {
                refreshPreview();
                scheduleSaveImportDefaults();
              }}
            >
              <option value="places">{'{ "places": { … } }  (RTDB import)'}</option>
              <option value="flat">{'{ … }  (children only)'}</option>
            </select>
          </div>

          <details>
            <summary>Field mapping reference</summary>
            <div className="legend" style={{ marginTop: 8 }}>
              OSM → your placeType:
              <br />
              • tourism=camp_site → <b>Campsite</b>
              <br />
              • tourism=caravan_site → <b>Caravan park</b>
              <br />
              • amenity=parking + motorhome → <b>Free/Paid motorhome place</b>
              <br />
              • amenity=parking + access=private → <b>Private parking lot</b>
              <br />
              • amenity=parking → <b>Parking lot</b>
              <br />
              • highway=services → <b>Motorway</b>
              <br />
              • highway=rest_area / tourism=picnic_site → <b>Rest area</b>
              <br />
              • tourism=viewpoint / leisure=nature_reserve / amenity=drinking_water → <b>Place in nature</b>
              <br />
              • highway=track + 4wd_only → <b>Off road 4x4</b>
              <br />
              <br />
              services ← toilets, shower, drinking_water, power_supply, dump, wifi, laundry, bbq
              <br />
              paymentStatus ← fee tag (Free / Paid / Unknown)
              <br />
              heightLimit ← maxheight tag
            </div>
          </details>
        </aside>

        {/* ============ MAIN : RESULTS + BULK + EXPORT ============ */}
        <main className="main">
          {/* bulk actions */}
          <div className="card" style={{ margin: 0 }}>
            <div className="bar">
              <h2 style={{ margin: 0 }}>
                Bulk actions{' '}
                <span className="count">
                  (<b>{selCount}</b> selected · <b>{selPending.length}</b> ready to upload)
                </span>
              </h2>
              <span className="spacer" />
              <button className="sm" onClick={() => selectAll(true)}>
                Select all
              </button>
              <button
                className="sm"
                disabled={!pendingCount}
                onClick={selectPending}
                title="Tick every location that has not been uploaded to the database yet"
              >
                Select not uploaded
              </button>
              <button className="sm" onClick={() => selectAll(false)}>
                Clear
              </button>
              <button className="sm danger" onClick={deleteSelected}>
                Delete selected
              </button>
            </div>
            <div className="grid2" style={{ marginTop: 10 }}>
              <div>
                <label>Set a field on selected</label>
                <div className="row">
                  <select ref={bulkFieldRef} defaultValue="placeType">
                    <option value="placeType">placeType</option>
                    <option value="paymentStatus">paymentStatus</option>
                    <option value="description">description</option>
                    <option value="addedBy">addedBy</option>
                    <option value="rating">rating</option>
                    <option value="heightLimit">heightLimit</option>
                  </select>
                  <input type="text" ref={bulkValueRef} placeholder="value" />
                  <button className="blue sm" onClick={bulkSet}>
                    Set
                  </button>
                </div>
              </div>
              <div>
                <label>Add / remove services &amp; activities on selected</label>
                <div className="row">
                  <select
                    value={arrField}
                    onChange={(e) => {
                      setArrField(e.target.value as 'services' | 'activities');
                      setArrPick([]); // the two lists have different values
                    }}
                  >
                    <option value="services">services</option>
                    <option value="activities">activities</option>
                  </select>
                  <button className="sm" onClick={() => setArrPickOpen((o) => !o)}>
                    {arrPickOpen ? '▾' : '▸'} Pick values ({arrPick.length})
                  </button>
                  <button className="sm" disabled={!arrPick.length} onClick={() => arrEdit(true)}>
                    Add
                  </button>
                  <button className="sm danger" disabled={!arrPick.length} onClick={() => arrEdit(false)}>
                    Remove
                  </button>
                </div>
                {!arrPickOpen && arrPick.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    <Chips arr={arrPick} />
                  </div>
                )}
                {arrPickOpen && (
                  <div style={{ marginTop: 6 }}>
                    <CheckboxPicker
                      key={arrField} /* switching field starts a fresh pick list */
                      title={arrField === 'services' ? 'Services' : 'Activities'}
                      options={arrField === 'services' ? serviceOptions : activityOptions}
                      values={arrPick}
                      instant
                      onChange={setArrPick}
                      onClose={() => setArrPickOpen(false)}
                    />
                  </div>
                )}
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              <label>
                Generate a field with GPT for all extracted locations (replaces existing values)
                {region ? ` · region: ${region}` : ''}
              </label>
              <div className="row" style={{ maxWidth: 460 }}>
                <select value={gptField} onChange={(e) => setGptField(e.target.value)}>
                  <option value="description">description</option>
                  <option value="placeType">placeType</option>
                  <option value="paymentStatus">paymentStatus</option>
                  <option value="services">services</option>
                  <option value="activities">activities</option>
                  <option value="heightLimit">heightLimit</option>
                  <option value="phone">phone</option>
                  <option value="website">website</option>
                  <option value="email">email</option>
                </select>
                <button className="blue" disabled={generating || !locations.length} onClick={generateForAll}>
                  {generating ? 'Generating…' : `✨ Generate for all (${locations.length})`}
                </button>
              </div>
              <button
                className="primary"
                style={{ marginTop: 8 }}
                disabled={generating || !locations.length}
                onClick={generateAllFields}
              >
                {generating ? 'Generating…' : `✨ Generate ALL fields for all (${locations.length})`}
              </button>
              <div className="hint">
                "Generate ALL fields" fills description, placeType, paymentStatus, services, activities & heightLimit
                in one pass — that's 6 GPT calls per location, plus phone/website/email for campsites, caravan
                parks, motorhome places, and any location with Laundrette, Bottled gas, LPG or Vehicle wash.
              </div>

              {/* editable AI prompts — per place-type prompt sets */}
              <div style={{ marginTop: 10 }}>
                <div className="row" style={{ alignItems: 'center' }}>
                  <button className="sm" onClick={() => setShowPrompts((s) => !s)}>
                    {showPrompts ? '▾' : '▸'} ✎ Customize AI prompts
                  </button>
                  {(() => {
                    const custom = Object.keys(promptSets).length + Object.keys(facetHints).length;
                    return custom > 0 ? (
                      <>
                        <span className="count">
                          {custom} customised
                        </span>
                        <button
                          className="sm danger"
                          onClick={() => {
                            setPromptSets({});
                            setFacetHints({});
                          }}
                        >
                          Reset all
                        </button>
                      </>
                    ) : null;
                  })()}
                  <span className="spacer" />
                  <span className="hint" style={{ margin: 0 }}>
                    {promptsSaved === 'saving'
                      ? 'Saving…'
                      : promptsSaved === 'saved'
                      ? '✓ Saved to your account'
                      : promptsSaved === 'error'
                      ? '⚠ Save failed'
                      : user
                      ? 'Saved to your account'
                      : 'Sign in to save'}
                  </span>
                </div>

                {showPrompts &&
                  (() => {
                    const isPlaceSet = PROMPT_SET_KEYS.includes(promptSet);
                    return (
                      <div className="promptbox">
                        <div className="hint" style={{ marginTop: 0 }}>
                          <b>Places</b> set the field prompts (untouched fields use the built-in wording).
                          <b> Services / activities</b> add a note that applies only when that checkbox governs the
                          search — priority is <b>Places &gt; Services &gt; Activities</b>. So a place search uses the
                          place prompt; a services-only search adds the ticked services' notes and sets place type
                          “Services”; an activities-only search adds the ticked activities' notes. Allowed enum values
                          are still enforced.
                        </div>

                        {/* set picker, grouped */}
                        <div className="setgroup">Places — full field prompts</div>
                        <div className="setpicker">
                          {PROMPT_SET_KEYS.map((k) => {
                            const edits = Object.keys(promptSets[k] || {}).length;
                            return (
                              <button
                                key={k}
                                className={'setchip' + (promptSet === k ? ' active' : '')}
                                onClick={() => setPromptSet(k)}
                                title={edits ? `${edits} field(s) customised` : 'using built-in defaults'}
                              >
                                {k}
                                {edits > 0 && <span className="dot" />}
                              </button>
                            );
                          })}
                        </div>

                        <div className="setgroup">Services / activities — extra note added to the description</div>
                        <div className="setpicker">
                          {[...SERVICE_LABELS, ...ACTIVITY_LABELS].map((k) => (
                            <button
                              key={k}
                              className={'setchip' + (promptSet === k ? ' active' : '')}
                              onClick={() => setPromptSet(k)}
                              title={facetHints[k] ? 'has a note' : 'no note'}
                            >
                              {k}
                              {facetHints[k] && <span className="dot" />}
                            </button>
                          ))}
                        </div>

                        {isPlaceSet ? (
                          <>
                            <div className="row" style={{ alignItems: 'center', marginTop: 6 }}>
                              <span className="hint" style={{ margin: 0 }}>
                                Editing <b>{promptSet}</b>
                              </span>
                              <span className="spacer" />
                              {Object.keys(promptSets[promptSet] || {}).length > 0 && (
                                <button className="sm danger" onClick={() => clearPromptSet(promptSet)}>
                                  Reset this set
                                </button>
                              )}
                            </div>

                            {PROMPT_FIELDS.map((f) => {
                              const edited = f in (promptSets[promptSet] || {});
                              return (
                                <div key={f} className="promptfield">
                                  <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <b>{f}</b>
                                    {edited && <span className="badge-edited">edited</span>}
                                    {edited && (
                                      <button className="linkbtn" onClick={() => clearPromptField(promptSet, f)}>
                                        reset
                                      </button>
                                    )}
                                  </label>
                                  <textarea
                                    value={shownPrompt(promptSet, f)}
                                    onChange={(e) => setPrompt(promptSet, f, e.target.value)}
                                    rows={f === 'description' ? 8 : 4}
                                  />
                                </div>
                              );
                            })}
                          </>
                        ) : (
                          <div className="promptfield">
                            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <b>{promptSet}</b>
                              <span className="count">extra description note</span>
                              {facetHints[promptSet] && (
                                <button className="linkbtn" onClick={() => setFacetHint(promptSet, '')}>
                                  clear
                                </button>
                              )}
                            </label>
                            <textarea
                              value={facetHints[promptSet] ?? ''}
                              onChange={(e) => setFacetHint(promptSet, e.target.value)}
                              rows={3}
                              placeholder={`Optional. Added to descriptions when you search by "${promptSet}" and no place type is ticked. e.g. "mention if the water is suitable for wild swimming."`}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })()}
              </div>
            </div>

            <div className="hint">
              dateAdded and lastUpdatedCostsDate are not editable here: both are stamped with the moment the location
              is written to the database (or exported), so the record's date is always the real one.
            </div>
            <div className="hint">
              Tip: click the coordinates to select and copy both numbers at once. Click any other cell to edit it
              inline. Services and activities open a checkbox list of every
              value the app's filters know (plus anything already in the data), so they can't drift from the app's
              enum. placeType/paymentStatus stay free text.
            </div>
          </div>

          {/* results table */}
          <div className="card" style={{ margin: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div className="bar" style={{ marginBottom: 10 }}>
              <h2 style={{ margin: 0 }}>Results</h2>
              <span className="count">
                <b>{locations.length}</b> locations · <b>{selPending.length}</b> selected for upload
                {uploadedCount ? ` · ${uploadedCount} already uploaded` : ''}
              </span>
              {country && (
                <span
                  className="count"
                  title="Only rows fetched under this country are listed, exported and uploaded"
                >
                  🌍 {countryName(country)}
                  {hiddenByCountry ? ` · ${hiddenByCountry} row(s) hidden` : ''}
                </span>
              )}
              {draftState !== 'idle' && (
                <span
                  className="count"
                  style={draftState === 'error' ? { color: '#c00' } : undefined}
                  title={
                    draftState === 'error'
                      ? `These rows could NOT be saved in this browser (${draftErr}) — download the .json so you don't lose them.`
                      : 'Kept in this browser, so closing the tab does not lose rows that were never uploaded'
                  }
                >
                  {draftState === 'saving' ? '💾 saving…' : draftState === 'saved' ? '💾 saved locally' : `⚠ not saved (${draftErr})`}
                </span>
              )}
              <span className="spacer" />
              <select
                value={payFilter}
                onChange={(e) => setPayFilter(e.target.value)}
                style={{ maxWidth: 150 }}
              >
                <option value="">All payments</option>
                <option value="Free">Free only</option>
                <option value="Paid">Paid only</option>
                <option value="Unknown">Unknown only</option>
              </select>
              <input
                type="text"
                value={quickFilter}
                onChange={(e) => setQuickFilter(e.target.value)}
                placeholder="filter by name / type…"
                style={{ maxWidth: 240 }}
              />
            </div>
            <Pager page={pg.page} pages={pg.pages} total={shown.length} setPage={pg.setPage} />
            <div className="tablewrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 28 }} title="Select the visible locations that are not uploaded yet">
                      <input
                        type="checkbox"
                        checked={shown.some((l) => !l._uploaded) && shown.every((l) => l._uploaded || l._sel)}
                        onChange={(e) => headerCheck(e.target.checked)}
                      />
                    </th>
                    <th style={{ width: 56 }}>photo</th>
                    <th>description</th>
                    <th>placeType</th>
                    <th>lat , lon</th>
                    <th>services</th>
                    <th>activities</th>
                    <th>payment</th>
                    <th>height</th>
                    <th>phone</th>
                    <th>website</th>
                    <th>email</th>
                    <th className="rowact" title="Remove the row from this list" />
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((l) => (
                    <tr key={l._id} className={`${l._uploaded ? 'up ' : ''}${l._dupWarning && !l._uploaded ? 'dup ' : ''}${l._sel ? 'sel' : ''}`.trim()}>
                      <td>
                        <input
                          type="checkbox"
                          checked={l._sel}
                          onChange={(e) => toggleRow(l._id, e.target.checked)}
                          title={l._uploaded ? 'Already uploaded — it is skipped on upload' : 'Select for upload'}
                        />
                      </td>
                      <td>
                        <Thumb l={l} onClick={() => openLightbox(l)} />
                      </td>
                      <EditableCell
                        l={l}
                        field="description"
                        editing={editing}
                        setEditing={setEditing}
                        commit={commitEdit}
                        render={(v) => (
                          <>
                            {escapeText(v)}
                            {l._src && (
                              <span
                                className={'srcbadge ' + l._src.replace('+', '-')}
                                title={
                                  l._src === 'osm+overture'
                                    ? 'Found in OSM, missing details filled from Overture'
                                    : l._src === 'overture'
                                    ? 'Found only in Overture Maps'
                                    : 'Found in OpenStreetMap'
                                }
                              >
                                {l._src === 'osm+overture' ? 'OSM+Overture' : l._src === 'overture' ? 'Overture' : 'OSM'}
                              </span>
                            )}
                            {l._uploaded && (
                              <div style={{ color: '#2e7d32', fontSize: 11, marginTop: 2 }}>✓ uploaded</div>
                            )}
                            {l._dupWarning && (
                              <div style={{ color: '#c00', fontSize: 11, marginTop: 2 }}>⚠ {l._dupWarning}</div>
                            )}
                            {l._photoWarning && !l._uploaded && (
                              <div style={{ color: '#c00', fontSize: 11, marginTop: 2 }}>⚠ {l._photoWarning}</div>
                            )}
                          </>
                        )}
                      />
                      <EditableCell
                        l={l}
                        field="placeType"
                        editing={editing}
                        setEditing={setEditing}
                        commit={commitEdit}
                        render={(v) => escapeText(v)}
                      />
                      <CoordCell lat={l.latitude} lon={l.longitude} />
                      <td
                        className="pickcell"
                        title="Click to tick services from the list"
                        onClick={() => setEditing({ id: l._id, field: 'services' })}
                      >
                        {editing?.id === l._id && editing.field === 'services' ? (
                          <CheckboxPicker
                            title="Services"
                            options={serviceOptions}
                            values={l.services || []}
                            instant
                            onChange={(next) => updateChips(l._id, 'services', next)}
                            onClose={() => setEditing(null)}
                          />
                        ) : (
                          <Chips arr={l.services} />
                        )}
                      </td>
                      <td
                        className="pickcell"
                        title="Click to tick activities from the list"
                        onClick={() => setEditing({ id: l._id, field: 'activities' })}
                      >
                        {editing?.id === l._id && editing.field === 'activities' ? (
                          <CheckboxPicker
                            title="Activities"
                            options={activityOptions}
                            values={l.activities || []}
                            instant
                            onChange={(next) => updateChips(l._id, 'activities', next)}
                            onClose={() => setEditing(null)}
                          />
                        ) : (
                          <Chips arr={l.activities} />
                        )}
                      </td>
                      <EditableCell
                        l={l}
                        field="paymentStatus"
                        editing={editing}
                        setEditing={setEditing}
                        commit={commitEdit}
                        render={(v) => <PayPill p={v} />}
                      />
                      <EditableCell
                        l={l}
                        field="heightLimit"
                        editing={editing}
                        setEditing={setEditing}
                        commit={commitEdit}
                        render={(v) => escapeText(v || '—')}
                      />
                      <EditableCell
                        l={l}
                        field="phone"
                        editing={editing}
                        setEditing={setEditing}
                        commit={commitEdit}
                        render={(v) => escapeText(v || '—')}
                      />
                      <EditableCell
                        l={l}
                        field="website"
                        editing={editing}
                        setEditing={setEditing}
                        commit={commitEdit}
                        render={(v) => escapeText(v || '—')}
                      />
                      <EditableCell
                        l={l}
                        field="email"
                        editing={editing}
                        setEditing={setEditing}
                        commit={commitEdit}
                        render={(v) => escapeText(v || '—')}
                      />
                      <td className="rowact">
                        <button
                          className="sm danger"
                          title={`Remove "${(l.description || '').slice(0, 60)}" from this list (nothing is deleted from the database)`}
                          onClick={() => deleteRow(l._id)}
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager page={pg.page} pages={pg.pages} total={shown.length} setPage={pg.setPage} />
          </div>

          {/* export */}
          <div className="card" style={{ margin: 0 }}>
            <div className="bar">
              <h2 style={{ margin: 0 }}>
                Export (your <span className="mono">places</span> structure)
              </h2>
              <span className="count">{exportCount}</span>
              <span className="spacer" />
              <button className="sm" onClick={refreshPreview}>
                Refresh preview
              </button>
              <button className="sm" onClick={copyJson}>
                Copy JSON
              </button>
              <button className="primary sm" onClick={downloadJson}>
                ⬇ Download .json
              </button>
              <button
                className="sm"
                disabled={uploading || !pendingCount}
                onClick={selectPending}
                title="Tick every location that has not been uploaded to the database yet"
              >
                ☑ Select not uploaded
              </button>
              <button
                className="sm"
                disabled={uploading || checking || !pendingCount}
                onClick={checkRows}
                title="Look for duplicates of live locations and for missing or corrupt photos — nothing is uploaded. Checks the ticked rows, or every not-uploaded row when none is ticked"
              >
                {checking ? 'Checking…' : `🔍 Check duplicates & photos (${selPending.length || pendingCount})`}
              </button>
              {shownDups.length > 0 && (
                <button
                  className="sm"
                  disabled={uploading || checking}
                  onClick={selectDuplicates}
                  title="Tick only the rows marked as duplicates, so they can be removed with Delete selected"
                >
                  ☑ Select duplicates ({shownDups.length})
                </button>
              )}
              {shownPhotoIssues.length > 0 && (
                <button
                  className="sm"
                  disabled={uploading || checking}
                  onClick={selectPhotoIssues}
                  title="Tick only the rows the check marked as having no photo or a broken one"
                >
                  ☑ Select photo problems ({shownPhotoIssues.length})
                </button>
              )}
              <button
                className="danger sm"
                disabled={uploading || checking || !selPending.length}
                onClick={uploadToFirebase}
                title={
                  mode === 'production'
                    ? 'Writes ONLY the ticked rows to places/ and places_locations/ — unticked rows stay in the table for further editing'
                    : 'Demo mode — switch to PRODUCTION (top right) to enable'
                }
              >
                {uploading ? 'Uploading…' : `⬆ Upload selected to Firebase (${selPending.length})`}
              </button>
              <button
                className="sm"
                disabled={uploading || !locations.length}
                onClick={clearResults}
                title="Empty the results table and map so you can run a fresh search"
              >
                🧹 Clear results
              </button>
            </div>
            <div className="hint">
              Only the ticked rows are uploaded. Anything you leave unticked stays in the table, keeps its edits and can
              be uploaded later — uploaded rows are greyed out, marked “✓ uploaded” and never sent twice. The whole
              table is also kept in this browser, so closing the tab or reloading brings the not-yet-uploaded rows back
              (“Clear results” is what throws them away).
            </div>
            {mode !== 'production' && (
              <div className="hint">
                Demo mode: uploading is disabled. Flip the switch in the header to PRODUCTION to write these locations
                to the live database (as user “{DEFAULT_UPLOADER}” unless you change addedBy).
              </div>
            )}
            <pre style={{ marginTop: 10 }}>{preview}</pre>
          </div>
        </main>
      </div>

      {/* photo browser */}
      {(() => {
        const row = lightboxId ? locations.find((l) => l._id === lightboxId) : null;
        if (!row || !imageCount(row)) return null;
        return (
          <PhotoLightbox
            l={row}
            onClose={() => setLightboxId(null)}
            // Uploaded rows are reordered in the "Manage uploads" tab instead.
            onReorder={row._uploaded ? undefined : (keys) => reorderLocalPhotos(row._id, keys)}
          />
        );
      })()}
    </div>
  );
};

/* ---------- shared photo browser (import table + Manage uploads) ---------- */
// With `manage` set (Manage uploads view) it also offers add / delete photo.
// With `onReorder` set it shows a thumbnail strip: drag a thumbnail (or use
// the move buttons) to change the photo order; it gets the keys in new order.
const PhotoLightbox: React.FC<{
  l: PlaceItem;
  onClose: () => void;
  manage?: { busy: boolean; onAdd: (files: FileList) => void; onDelete: (imageKey: string) => void };
  onReorder?: (keys: string[]) => void;
  reorderBusy?: boolean;
}> = ({ l, onClose, manage, onReorder, reorderBusy }) => {
  const [idxRaw, setIdx] = useState(0);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const entries = orderedImages(l.images);
  const imgs = entries.map(([, v]) => v);
  const count = imgs.length;
  // Move photo `from` to position `to` and keep showing it.
  const move = (from: number, to: number) => {
    if (!onReorder || reorderBusy || from === to || to < 0 || to >= count) return;
    const keys = entries.map(([k]) => k);
    const [k] = keys.splice(from, 1);
    keys.splice(to, 0, k);
    onReorder(keys);
    setIdx(to);
  };

  // Keyboard navigation (← → arrows, Esc to close).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') setIdx((i) => (i + 1) % count);
      else if (e.key === 'ArrowLeft') setIdx((i) => (i - 1 + count) % count);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [count, onClose]);

  if (!count) return null;
  const idx = Math.min(idxRaw, count - 1);
  const cur = imgs[idx];
  const isSat = /arcgisonline\.com/.test(cur.url);
  const prev = () => setIdx((i) => (i - 1 + count) % count);
  const next = () => setIdx((i) => (i + 1) % count);
  return (
    <div className="lightbox" onClick={onClose}>
      <div className="lb-stage" onClick={(e) => e.stopPropagation()}>
        {count > 1 && (
          <button className="lb-nav lb-prev" onClick={prev} aria-label="Previous">
            ‹
          </button>
        )}
        <img
          src={cur.url}
          alt=""
          className="lb-img"
          onError={(e) => {
            (e.target as HTMLImageElement).style.visibility = 'hidden';
          }}
        />
        {count > 1 && (
          <button className="lb-nav lb-next" onClick={next} aria-label="Next">
            ›
          </button>
        )}
        <div className="lb-caption">
          {idx + 1} / {count} · {isSat ? 'Satellite' : 'Photo'}
          {count > 1 ? ' · use ← → to browse' : ''} · click outside to close
        </div>
        {onReorder && count > 1 && (
          <>
            <div className="lb-strip">
              {entries.map(([k, v], i) => (
                <img
                  key={k}
                  src={v.url}
                  alt=""
                  draggable={!reorderBusy}
                  title={`Photo ${i + 1} — drag to reorder, click to view`}
                  className={`lb-sthumb${i === idx ? ' cur' : ''}${dragOver === i && dragFrom !== i ? ' over' : ''}${
                    dragFrom === i ? ' dragging' : ''
                  }`}
                  onClick={() => setIdx(i)}
                  onDragStart={(e) => {
                    setDragFrom(i);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(i);
                  }}
                  onDragLeave={() => setDragOver((o) => (o === i ? null : o))}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragFrom !== null) move(dragFrom, i);
                    setDragFrom(null);
                    setDragOver(null);
                  }}
                  onDragEnd={() => {
                    setDragFrom(null);
                    setDragOver(null);
                  }}
                />
              ))}
            </div>
            <div className="lb-actions">
              <button className="lb-btn" disabled={reorderBusy || idx === 0} onClick={() => move(idx, idx - 1)}>
                ◀ Move left
              </button>
              <button className="lb-btn" disabled={reorderBusy || idx === 0} onClick={() => move(idx, 0)}>
                ★ Make first
              </button>
              <button className="lb-btn" disabled={reorderBusy || idx === count - 1} onClick={() => move(idx, idx + 1)}>
                Move right ▶
              </button>
            </div>
          </>
        )}
        {manage && (
          <div className="lb-actions">
            <label className="lb-btn">
              ＋ Add photos
              <input
                type="file"
                multiple
                accept="image/*"
                style={{ display: 'none' }}
                disabled={manage.busy}
                onChange={(e) => {
                  if (e.target.files?.length) manage.onAdd(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
            <button
              className="lb-btn danger"
              disabled={manage.busy}
              onClick={() => manage.onDelete(entries[idx][0])}
            >
              {manage.busy ? 'Working…' : '🗑 Delete this photo'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ============================================================
   Manage uploads — every live location added by a given user
   (default "Karve"): inline edit any field, or delete it.
   Writes only happen in PRODUCTION mode.
   ============================================================ */
const MANAGE_NUMERIC = ['rating', 'latitude', 'longitude'];
const MANAGE_ARRAYS = ['services', 'activities'];

/* Paging straight from the database, newest first: only the pages visited
   are ever held, so a million locations cost what a hundred do. Rows come
   in key order — push ids start with their creation time, so that is
   upload order — through the addedBy index (".indexOn": ["addedBy"] under
   "places"). A date filter is a key range, so the database narrows it; text
   and country can't be indexed, so those read the user's rows in chunks and
   keep the matches until a page is full. */
const MANAGE_PAGE = 100;
const SCAN_CHUNK = 500; // rows read per query while a text / country filter is on
const PUSH_KEY_MAX_TAIL = 'zzzzzzzzzzzz'; // highest 12-char random part of a push id

// The 8-char time prefix of push ids made at `ms`.
function pushKeyAt(ms: number) {
  let s = '';
  let n = Math.max(0, Math.floor(ms));
  for (let i = 0; i < 8; i++) {
    s = PUSH_CHARS.charAt(n % 64) + s;
    n = Math.floor(n / 64);
  }
  return s;
}
// "18/07/2026", "07/2026", "2026-07-18", "2026-07" or "2026" → [start, end)
// in ms, local time; anything else is a text filter (null).
function parseDateFilter(s: string): [number, number] | null {
  const t = s.trim();
  let m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/) || t.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const [d, mo, y] = t.includes('/') ? [+m[1], +m[2], +m[3]] : [+m[3], +m[2], +m[1]];
    return [new Date(y, mo - 1, d).getTime(), new Date(y, mo - 1, d + 1).getTime()];
  }
  m = t.match(/^(\d{1,2})\/(\d{4})$/) || t.match(/^(\d{4})-(\d{2})$/);
  if (m) {
    const [mo, y] = t.includes('/') ? [+m[1], +m[2]] : [+m[2], +m[1]];
    return [new Date(y, mo - 1, 1).getTime(), new Date(y, mo, 1).getTime()];
  }
  if (/^\d{4}$/.test(t)) return [new Date(+t, 0, 1).getTime(), new Date(+t + 1, 0, 1).getTime()];
  return null;
}
function manageRow(k: string, v: any): PlaceItem {
  return {
    ...v,
    _id: k,
    _sel: false,
    _osm: { type: 'db', id: 0, tags: {} },
    services: v?.services || [],
    activities: v?.activities || [],
    images: v?.images || {},
  };
}
interface ManageScan {
  gen: number; // bumped by every new scan; stale results are dropped
  who: string;
  lo?: string; // key range from a date filter
  hi?: string;
  cursor: string | null; // oldest key read so far; the next read ends there
  done: boolean;
  stop: boolean;
  buffer: PlaceItem[]; // matches not on a page yet
  scanned: number;
  filtered: boolean;
  match: (r: PlaceItem) => boolean;
}

const ManageView: React.FC<{ mode: 'demo' | 'production' }> = ({ mode }) => {
  const [uploader, setUploader] = useState(DEFAULT_UPLOADER);
  // Visited pages only. Edits, deletes and ticks apply to every page by id.
  const [pages, setPages] = useState<PlaceItem[][]>([]);
  const [pageIdx, setPageIdx] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ msg: string; cls: string }>({ msg: 'Click "Load" to fetch locations.', cls: '' });
  const [editing, setEditing] = useState<{ id: string; field: string } | null>(null);
  // The lightbox tracks the row id (not a snapshot) so photo adds/deletes
  // made while it is open show up immediately.
  const [lightboxId, setLightboxId] = useState<string | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [filter, setFilter] = useState('');
  // Country limit. Uploaded rows carry no country, so each point is tested
  // against the country's outline (fetched once).
  const [country, setCountry] = useState('');
  const [rings, setRings] = useState<CountryShape | null>(null);
  const [ringsBusy, setRingsBusy] = useState(false);

  const rows = useMemo(() => pages[pageIdx] || [], [pages, pageIdx]);
  const allRows = useMemo(() => pages.flat(), [pages]);
  const setRows = useCallback((fn: (prev: PlaceItem[]) => PlaceItem[]) => setPages((ps) => ps.map(fn)), []);
  const pagesRef = useRef(pages);
  pagesRef.current = pages;
  const scanRef = useRef<ManageScan | null>(null);

  // Reads until one more page of matches is ready (or the list ends / Stop),
  // then shows it.
  const fillNextPage = useCallback(async () => {
    const s = scanRef.current;
    if (!s) return;
    setLoading(true);
    try {
      while (s.buffer.length < MANAGE_PAGE && !s.done && !s.stop) {
        const chunk = s.filtered ? SCAN_CHUNK : MANAGE_PAGE;
        // The cursor row itself comes back again (bounds are inclusive).
        const want = chunk + (s.cursor ? 1 : 0);
        const upper = s.cursor ?? s.hi;
        const snap = await get(
          query(
            ref(db, 'places'),
            orderByChild('addedBy'),
            s.lo ? startAt(s.who, s.lo) : startAt(s.who),
            upper ? endAt(s.who, upper) : endAt(s.who),
            limitToLast(want)
          )
        );
        if (scanRef.current?.gen !== s.gen) return; // a newer scan took over
        const got: PlaceItem[] = [];
        snap.forEach((c) => {
          got.push(manageRow(c.key as string, c.val()));
        });
        got.reverse(); // newest first
        if (got.length < want) s.done = true;
        const fresh = s.cursor ? got.filter((r) => r._id !== s.cursor) : got;
        if (got.length) s.cursor = got[got.length - 1]._id;
        s.scanned += fresh.length;
        for (const r of fresh) if (s.match(r)) s.buffer.push(r);
        if (s.filtered)
          setMsg({ msg: `Searching… ${s.scanned} location(s) read, ${s.buffer.length} match(es) for this page.`, cls: '' });
      }
      s.stop = false;
      const page = s.buffer.splice(0, MANAGE_PAGE);
      const more = s.buffer.length > 0 || !s.done;
      if (page.length || !pagesRef.current.length) {
        setPages((ps) => [...ps, page]);
        setPageIdx(pagesRef.current.length);
      }
      setHasMore(more);
      const n = pagesRef.current.length + (page.length || !pagesRef.current.length ? 1 : 0);
      setMsg({
        msg:
          (page.length
            ? `Page ${n}: ${page.length} location(s) added by "${s.who}"`
            : `No ${pagesRef.current.length ? 'more ' : ''}locations added by "${s.who}"${s.filtered || s.lo ? ' match the filter' : ''}`) +
          (s.filtered ? ` — ${s.scanned} read so far` : '') +
          (more ? '.' : ' · end of the list.'),
        cls: 'ok',
      });
    } catch (e: any) {
      if (scanRef.current?.gen !== s.gen) return;
      setMsg({
        msg: /index/i.test(String(e?.message))
          ? 'Load failed: the database has no index on addedBy — add ".indexOn": ["addedBy"] under "places" in the Realtime Database rules (without it every place would have to be downloaded).'
          : 'Load failed: ' + (e?.message || e),
        cls: 'err',
      });
    } finally {
      if (scanRef.current?.gen === s.gen) setLoading(false);
    }
  }, []);

  const startScan = useCallback(
    (who: string) => {
      const range = parseDateFilter(filter);
      const text = range ? '' : filter.trim().toLowerCase();
      const area = country ? rings : null;
      scanRef.current = {
        gen: (scanRef.current?.gen ?? 0) + 1,
        who,
        lo: range ? pushKeyAt(range[0]) : undefined,
        hi: range ? pushKeyAt(range[1] - 1) + PUSH_KEY_MAX_TAIL : undefined,
        cursor: null,
        done: false,
        stop: false,
        buffer: [],
        scanned: 0,
        filtered: !!(text || area),
        match: (r) =>
          (!area || inCountry(r.latitude, r.longitude, area)) &&
          (!text ||
            (r.description || '').toLowerCase().includes(text) ||
            (r.placeType || '').toLowerCase().includes(text)),
      };
      pagesRef.current = [];
      setPages([]);
      setPageIdx(0);
      setHasMore(false);
      setLoadedFor(who);
      setMsg({ msg: `Loading locations added by "${who}"…`, cls: '' });
      fillNextPage();
    },
    [filter, country, rings, fillNextPage]
  );
  const load = startScan;

  // Filters re-run the query once typing pauses; this also does the first load.
  const whoRef = useRef(uploader);
  whoRef.current = loadedFor ?? uploader;
  useEffect(() => {
    if (country && !rings) return; // outline still loading
    const t = window.setTimeout(() => startScan(whoRef.current), 450);
    return () => window.clearTimeout(t);
  }, [startScan, country, rings]);

  const nextPage = () => {
    if (pageIdx + 1 < pages.length) setPageIdx(pageIdx + 1);
    else if (hasMore && !loading) fillNextPage();
  };
  const stopScan = () => {
    if (scanRef.current) scanRef.current.stop = true;
  };

  const commitEdit = async (id: string, field: string, value: string) => {
    setEditing(null);
    const row = rows.find((r) => r._id === id);
    if (!row) return;
    let v: any = value;
    if (MANAGE_NUMERIC.includes(field)) {
      v = Number(value);
      if (Number.isNaN(v)) {
        setMsg({ msg: `"${value}" is not a number — ${field} not saved.`, cls: 'err' });
        return;
      }
    }
    if (MANAGE_ARRAYS.includes(field)) v = parseList(value);
    // Same tidy-up as the import side, so edits can't reintroduce spaces and
    // dashes the rest of the database does not have.
    if (field === 'phone') v = normalizePhone(value, country);
    if (String(row[field] ?? '') === String(v)) return; // nothing changed
    if (mode !== 'production') {
      setMsg({ msg: 'Demo mode — change NOT saved. Switch to PRODUCTION (top right) to edit live data.', cls: 'err' });
      return;
    }
    try {
      const updates: Record<string, any> = { [`places/${id}/${field}`]: v };
      // Keep the geo index in sync when coordinates change.
      if (field === 'latitude' || field === 'longitude') {
        const lat = field === 'latitude' ? v : row.latitude;
        const lon = field === 'longitude' ? v : row.longitude;
        updates[`places_locations/${id}`] = { g: geohash(lat, lon), l: [lat, lon] };
      }
      await update(ref(db), updates);
      setRows((prev) => prev.map((r) => (r._id === id ? { ...r, [field]: v } : r)));
      setMsg({ msg: `Saved ${field}.`, cls: 'ok' });
    } catch (e: any) {
      setMsg({ msg: `Save failed: ${e?.message || e}`, cls: 'err' });
    }
  };

  const deleteRow = async (id: string) => {
    const row = rows.find((r) => r._id === id);
    if (mode !== 'production') {
      setMsg({ msg: 'Demo mode — switch to PRODUCTION (top right) to delete live data.', cls: 'err' });
      return;
    }
    const name = (row?.description || id).slice(0, 80);
    if (!window.confirm(`Permanently DELETE this location from the live database?\n\n"${name}"`)) return;
    try {
      await update(ref(db), { [`places/${id}`]: null, [`places_locations/${id}`]: null });
      setRows((prev) => prev.filter((r) => r._id !== id));
      setMsg({ msg: 'Location deleted.', cls: 'ok' });
    } catch (e: any) {
      setMsg({ msg: `Delete failed: ${e?.message || e}`, cls: 'err' });
    }
  };

  const toggleRow = (id: string, val: boolean) => {
    setRows((prev) => prev.map((r) => (r._id === id ? { ...r, _sel: val } : r)));
  };
  // `ids` scopes select-all to the currently filtered/shown rows, not the
  // whole loaded set, so filtering then "select all" only grabs what's visible.
  const selectAllShown = (ids: string[], val: boolean) => {
    const idSet = new Set(ids);
    setRows((prev) => prev.map((r) => (idSet.has(r._id) ? { ...r, _sel: val } : r)));
  };

  const deleteSelected = async () => {
    if (mode !== 'production') {
      setMsg({ msg: 'Demo mode — switch to PRODUCTION (top right) to delete live data.', cls: 'err' });
      return;
    }
    const sel = allRows.filter((r) => r._sel);
    if (!sel.length) return;
    const names = sel.map((r) => (r.description || r._id).slice(0, 60));
    const preview = names.slice(0, 5).join('\n') + (names.length > 5 ? `\n…and ${names.length - 5} more` : '');
    if (!window.confirm(`Permanently DELETE ${sel.length} location(s) from the live database?\n\n${preview}`)) return;
    try {
      const updates: Record<string, any> = {};
      for (const r of sel) {
        updates[`places/${r._id}`] = null;
        updates[`places_locations/${r._id}`] = null;
      }
      await update(ref(db), updates);
      const deletedIds = new Set(sel.map((r) => r._id));
      setRows((prev) => prev.filter((r) => !deletedIds.has(r._id)));
      setMsg({ msg: `Deleted ${sel.length} location(s).`, cls: 'ok' });
    } catch (e: any) {
      setMsg({ msg: `Bulk delete failed: ${e?.message || e}`, cls: 'err' });
    }
  };

  const [photoBackfillBusy, setPhotoBackfillBusy] = useState(false);

  const pickCountry = async (code: string) => {
    setCountry(code);
    setRings(null);
    if (!code) return;
    setRingsBusy(true);
    setMsg({ msg: `Loading the outline of ${countryName(code)}…`, cls: '' });
    try {
      const r = await countryShape(code);
      setRings(r);
      setMsg({
        msg:
          `Showing locations inside ${countryName(code)} only.` +
          (r.fallback ? ' (Approximate outline — the OpenStreetMap outline service is unreachable.)' : ''),
        cls: 'ok',
      });
    } catch (e: any) {
      setCountry('');
      setMsg({
        msg: `Could not load the outline of ${countryName(code)} (${e?.message || e}) — country filter left off.`,
        cls: 'err',
      });
    } finally {
      setRingsBusy(false);
    }
  };

  // Same checkbox lists as the import table, built from the live rows.
  const svcOptions = useMemo(
    () => mergeOptions(SERVICE_OPTIONS, rows.flatMap((r) => r.services || [])),
    [rows]
  );
  const actOptions = useMemo(
    () => mergeOptions(ACTIVITY_OPTIONS, rows.flatMap((r) => r.activities || [])),
    [rows]
  );
  // Recovery tool: regenerate the (deterministic) satellite URL from a
  // location's own lat/lon and retry the copy-into-Storage step for selected
  // rows. For locations uploaded while the server's photo proxy was failing
  // (e.g. admin verification misconfigured), the place record went in with
  // no images and the original satellite URL was never persisted — this
  // rebuilds it instead of requiring the whole search+upload to be redone.
  const backfillSatellitePhoto = async () => {
    if (mode !== 'production') {
      setMsg({ msg: 'Demo mode — switch to PRODUCTION (top right) to add photos.', cls: 'err' });
      return;
    }
    const sel = allRows.filter((r) => r._sel);
    if (!sel.length) return;
    setPhotoBackfillBusy(true);
    // Read the user's saved "Satellite area (metres across)" setting (set on
    // the Import tab) instead of hardcoding a value here.
    let satMeters = 200;
    try {
      const uid = auth.currentUser?.uid;
      if (uid) {
        const snap = await get(ref(db, `users/${uid}/importDefaults`));
        if (snap.exists()) {
          const raw = snap.val();
          const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
          if (data && typeof data.satMeters === 'number') satMeters = data.satMeters;
        }
      }
    } catch {
      /* ignore — fall back to default */
    }
    let done = 0;
    let failed = 0;
    let lastErr = '';
    await pool(sel, 4, async (row) => {
      try {
        const url = satUrl(row.latitude, row.longitude, satMeters);
        const blob = await fetchImageBlobWithRetry(url);
        const problem = await imageProblem(blob);
        if (problem) throw new Error(`satellite photo rejected — ${problem}`);
        const key = `image_${nextImageNum(row.images)}`;
        const objRef = sRef(storage, `places/${row._id}/${key}.jpg`);
        await uploadBytes(objRef, blob, { contentType: blob.type || 'image/jpeg' });
        const entry: ImageEntry = {
          dateUploaded: fmtDateSlash(new Date()),
          uploadedBy: uploader,
          url: await getDownloadURL(objRef),
        };
        await update(ref(db), { [`places/${row._id}/images/${key}`]: entry });
        setRows((prev) =>
          prev.map((r) => (r._id === row._id ? { ...r, images: { ...r.images, [key]: entry } } : r))
        );
        done++;
      } catch (e: any) {
        failed++;
        lastErr = e?.message || String(e);
        console.warn(`[backfill] satellite photo failed for ${row._id} (${row.description}): ${lastErr}`);
      }
    });
    setPhotoBackfillBusy(false);
    setMsg({
      msg: failed
        ? `Added satellite photo to ${done}, FAILED ${failed} (${lastErr}). See browser console for per-location errors.`
        : `Added satellite photo to ${done} location(s).`,
      cls: failed ? 'err' : 'ok',
    });
  };

  // Upload new photos into Firebase Storage (places/{id}/image_N.jpg — the
  // same layout the mobile app uses) and register them under images/ in the DB.
  const addPhotos = async (id: string, files: FileList) => {
    if (mode !== 'production') {
      // alert(), not the status line — that line is hidden behind the photo
      // overlay, which made this look like the button simply doesn't work.
      window.alert('Demo mode — switch to PRODUCTION (top right of the header) to add photos.');
      return;
    }
    const row = rows.find((r) => r._id === id);
    if (!row || !files.length) return;
    setPhotoBusy(true);
    try {
      // Continue numbering after the highest existing image_N.
      let next = nextImageNum(row.images);
      const updates: Record<string, any> = {};
      const added: Record<string, ImageEntry> = {};
      const rejected: string[] = [];
      let n = 0;
      for (const file of Array.from(files)) {
        setMsg({ msg: `Uploading photo ${++n}/${files.length}…`, cls: '' });
        // Checked before the resize, because downscaleImage quietly falls back
        // to the original file when it cannot decode one.
        const problem = await imageProblem(file);
        if (problem) {
          rejected.push(`${file.name}: ${problem}`);
          continue;
        }
        const blob = await downscaleImage(file);
        const key = `image_${next++}`;
        const objRef = sRef(storage, `places/${id}/${key}.jpg`);
        await uploadBytes(objRef, blob, { contentType: 'image/jpeg' });
        const url = await getDownloadURL(objRef);
        const entry: ImageEntry = { dateUploaded: fmtDateSlash(new Date()), uploadedBy: uploader, url };
        updates[`places/${id}/images/${key}`] = entry;
        added[key] = entry;
      }
      if (Object.keys(updates).length) await update(ref(db), updates);
      setRows((prev) => prev.map((r) => (r._id === id ? { ...r, images: { ...r.images, ...added } } : r)));
      const okCount = Object.keys(added).length;
      if (rejected.length) {
        // The status line sits behind the photo overlay, so say it out loud.
        const why = `${rejected.length} file(s) rejected as unusable: ${rejected.join('; ')}`;
        setMsg({ msg: `Added ${okCount} photo(s). ${why}.`, cls: 'err' });
        window.alert(why);
      } else {
        setMsg({ msg: `Added ${okCount} photo(s).`, cls: 'ok' });
      }
    } catch (e: any) {
      setMsg({ msg: `Photo upload failed: ${e?.message || e}`, cls: 'err' });
      window.alert(`Photo upload failed: ${e?.message || e}`);
    }
    setPhotoBusy(false);
  };

  // Save a new photo order: the images node is rewritten as image_0..N-1 in
  // that order (the Storage files and their URLs stay as they are).
  const reorderPhotos = async (id: string, keys: string[]) => {
    if (mode !== 'production') {
      window.alert('Demo mode — switch to PRODUCTION (top right of the header) to reorder photos.');
      return;
    }
    const row = rows.find((r) => r._id === id);
    if (!row) return;
    const before = row.images || {};
    const images = renumberImages(before, keys);
    setRows((prev) => prev.map((r) => (r._id === id ? { ...r, images } : r)));
    setPhotoBusy(true);
    try {
      await update(ref(db), { [`places/${id}/images`]: images });
      setMsg({ msg: 'Photo order saved.', cls: 'ok' });
    } catch (e: any) {
      setRows((prev) => prev.map((r) => (r._id === id ? { ...r, images: before } : r)));
      setMsg({ msg: `Photo reorder failed: ${e?.message || e}`, cls: 'err' });
      window.alert(`Photo reorder failed: ${e?.message || e}`);
    }
    setPhotoBusy(false);
  };

  const deletePhoto = async (id: string, key: string) => {
    if (mode !== 'production') {
      window.alert('Demo mode — switch to PRODUCTION (top right of the header) to delete photos.');
      return;
    }
    const row = rows.find((r) => r._id === id);
    const img = row?.images?.[key];
    if (!img) return;
    if (!window.confirm('Delete this photo from the location?')) return;
    setPhotoBusy(true);
    try {
      await update(ref(db), { [`places/${id}/images/${key}`]: null });
      // Also remove the file from Storage when it lives in our bucket
      // (external URLs — satellite, OSM photos — have nothing to delete).
      if (/firebasestorage\.googleapis\.com/.test(img.url)) {
        try {
          await deleteObject(sRef(storage, img.url));
        } catch {
          /* already gone or no permission — the DB entry is what matters */
        }
      }
      setRows((prev) =>
        prev.map((r) => {
          if (r._id !== id) return r;
          const images = { ...r.images };
          delete images[key];
          return { ...r, images };
        })
      );
      setMsg({ msg: 'Photo deleted.', cls: 'ok' });
    } catch (e: any) {
      setMsg({ msg: `Photo delete failed: ${e?.message || e}`, cls: 'err' });
      window.alert(`Photo delete failed: ${e?.message || e}`);
    }
    setPhotoBusy(false);
  };

  // Filtering happens in the query (startScan) — the page shows as fetched.
  const shown = rows;
  const pageRows = rows;
  const manageSelCount = useMemo(() => allRows.filter((r) => r._sel).length, [allRows]);
  // No total: counting a million rows would mean reading them all.
  const pager = (
    <div className="pager">
      <button className="sm" disabled={pageIdx === 0} onClick={() => setPageIdx(0)} title="First page">
        «
      </button>
      <button className="sm" disabled={pageIdx === 0} onClick={() => setPageIdx(pageIdx - 1)}>
        ‹ Prev
      </button>
      <span className="count">
        page <b>{pageIdx + 1}</b>
        {pages.length > 1 ? ` of ${pages.length} loaded` : ''}
        {!hasMore && pageIdx === pages.length - 1 ? ' · end of the list' : ''}
      </span>
      <button className="sm" disabled={loading || (pageIdx >= pages.length - 1 && !hasMore)} onClick={nextPage}>
        {loading ? 'Loading…' : 'Next ›'}
      </button>
      {loading && scanRef.current?.filtered && (
        <button className="sm danger" onClick={stopScan} title="Stop searching and show the matches found so far">
          Stop
        </button>
      )}
    </div>
  );

  return (
    <div className="managewrap">
      <div className="card">
        <div className="bar">
          <h2 style={{ margin: 0 }}>Uploaded locations</h2>
          <label style={{ margin: 0 }}>by user</label>
          <input
            type="text"
            value={uploader}
            onChange={(e) => setUploader(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') load(uploader);
            }}
            style={{ maxWidth: 160 }}
          />
          <button className="blue sm" disabled={loading} onClick={() => load(uploader)}>
            {loading ? 'Loading…' : '⟳ Load'}
          </button>
          <label style={{ margin: 0 }}>country</label>
          <select
            value={country}
            disabled={ringsBusy}
            onChange={(e) => pickCountry(e.target.value)}
            style={{ maxWidth: 170 }}
            title="Show only the locations whose coordinates fall inside this country"
          >
            <option value="">Any country</option>
            <optgroup label="Nearby">
              {COUNTRIES.filter((c) => NEARBY_CODES.includes(c.code)).map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </optgroup>
            <optgroup label="Europe">
              {COUNTRIES.filter((c) => !NEARBY_CODES.includes(c.code)).map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </optgroup>
          </select>
          {ringsBusy && <span className="count">loading outline…</span>}
          <span className="count">
            <b>{shown.length}</b>
            {' '}on page {pageIdx + 1}
          </span>
          {manageSelCount > 0 && (
            <>
              <span className="count">
                <b>{manageSelCount}</b> selected
              </span>
              <button className="sm danger" disabled={mode !== 'production'} onClick={deleteSelected}>
                🗑 Delete selected ({manageSelCount})
              </button>
              <button
                className="sm"
                disabled={mode !== 'production' || photoBackfillBusy}
                onClick={backfillSatellitePhoto}
                title="Regenerate the satellite photo from each location's coordinates and upload it — for locations that ended up with no photo"
              >
                {photoBackfillBusy ? 'Adding photos…' : `🛰 Add satellite photo (${manageSelCount})`}
              </button>
              <button className="sm" onClick={() => selectAllShown(shown.map((r) => r._id), false)}>
                Clear selection
              </button>
            </>
          )}
          <span className="spacer" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="filter: name / type, or a date 18/07/2026 · 07/2026 · 2026…"
            style={{ maxWidth: 280 }}
          />
        </div>
        <div className={'status ' + msg.cls} style={{ marginTop: 8 }}>
          {msg.msg}
          {mode !== 'production' && ' · DEMO mode: editing and deleting are disabled.'}
        </div>
        <div className="hint">
          Click any cell to edit it (Enter saves, Esc cancels) — changes are written straight to the live database.
          services / activities are edited as a comma-separated list. Editing latitude/longitude also updates the
          map index (places_locations).
        </div>
      </div>

      <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {pager}
        <div className="tablewrap" style={{ maxHeight: 'none', flex: 1, minHeight: 0 }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 28 }} title="Select every location on this page (ticks on other pages stay)">
                  <input
                    type="checkbox"
                    checked={shown.length > 0 && shown.every((l) => l._sel)}
                    onChange={(e) => selectAllShown(shown.map((r) => r._id), e.target.checked)}
                  />
                </th>
                <th style={{ width: 56 }}>photo</th>
                <th>description</th>
                <th>placeType</th>
                <th>lat</th>
                <th>lon</th>
                <th>payment</th>
                <th>services</th>
                <th>activities</th>
                <th>rating</th>
                <th>height</th>
                <th>phone</th>
                <th>website</th>
                <th>email</th>
                <th>added</th>
                <th className="rowact" title="Delete from the live database" />
              </tr>
            </thead>
            <tbody>
              {pageRows.map((l) => (
                <tr key={l._id} className={l._sel ? 'sel' : ''}>
                  <td>
                    <input type="checkbox" checked={!!l._sel} onChange={(e) => toggleRow(l._id, e.target.checked)} />
                  </td>
                  <td
                    onClick={() => imageCount(l) && setLightboxId(l._id)}
                    style={imageCount(l) ? { cursor: 'zoom-in' } : undefined}
                    title={imageCount(l) ? `${imageCount(l)} photo(s) — click to view / manage` : 'No photos yet'}
                  >
                    <Thumb l={l} onClick={() => imageCount(l) && setLightboxId(l._id)} />
                    <label
                      className="addphoto"
                      title="Add photos to this location"
                      onClick={(e) => e.stopPropagation()}
                    >
                      ＋ photo
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        style={{ display: 'none' }}
                        disabled={photoBusy}
                        onChange={(e) => {
                          if (e.target.files?.length) addPhotos(l._id, e.target.files);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </td>
                  <EditableCell l={l} field="description" editing={editing} setEditing={setEditing} commit={commitEdit} render={(v) => escapeText(v)} />
                  <EditableCell l={l} field="placeType" editing={editing} setEditing={setEditing} commit={commitEdit} render={(v) => escapeText(v)} />
                  <EditableCell
                    l={l}
                    field="latitude"
                    editing={editing}
                    setEditing={setEditing}
                    commit={commitEdit}
                    render={(v) => (
                      <span className="mono">
                        {v}
                        <CopyCoords lat={l.latitude} lon={l.longitude} />
                      </span>
                    )}
                  />
                  <EditableCell
                    l={l}
                    field="longitude"
                    editing={editing}
                    setEditing={setEditing}
                    commit={commitEdit}
                    render={(v) => (
                      <span className="mono">
                        {v}
                        <CopyCoords lat={l.latitude} lon={l.longitude} />
                      </span>
                    )}
                  />
                  <EditableCell l={l} field="paymentStatus" editing={editing} setEditing={setEditing} commit={commitEdit} render={(v) => <PayPill p={v} />} />
                  <td
                    className="pickcell"
                    title="Click to tick services from the list"
                    onClick={() => setEditing({ id: l._id, field: 'services' })}
                  >
                    {editing?.id === l._id && editing.field === 'services' ? (
                      <CheckboxPicker
                        title="Services"
                        options={svcOptions}
                        values={l.services || []}
                        onChange={(next) => commitEdit(l._id, 'services', next.join(','))}
                        onClose={() => setEditing(null)}
                      />
                    ) : (
                      <Chips arr={l.services} />
                    )}
                  </td>
                  <td
                    className="pickcell"
                    title="Click to tick activities from the list"
                    onClick={() => setEditing({ id: l._id, field: 'activities' })}
                  >
                    {editing?.id === l._id && editing.field === 'activities' ? (
                      <CheckboxPicker
                        title="Activities"
                        options={actOptions}
                        values={l.activities || []}
                        onChange={(next) => commitEdit(l._id, 'activities', next.join(','))}
                        onClose={() => setEditing(null)}
                      />
                    ) : (
                      <Chips arr={l.activities} />
                    )}
                  </td>
                  <EditableCell l={l} field="rating" editing={editing} setEditing={setEditing} commit={commitEdit} render={(v) => escapeText(v ?? 0)} />
                  <EditableCell l={l} field="heightLimit" editing={editing} setEditing={setEditing} commit={commitEdit} render={(v) => escapeText(v || '—')} />
                  <EditableCell l={l} field="phone" editing={editing} setEditing={setEditing} commit={commitEdit} render={(v) => escapeText(v || '—')} />
                  <EditableCell l={l} field="website" editing={editing} setEditing={setEditing} commit={commitEdit} render={(v) => escapeText(v || '—')} />
                  <EditableCell l={l} field="email" editing={editing} setEditing={setEditing} commit={commitEdit} render={(v) => escapeText(v || '—')} />
                  <td className="mono">{(l.dateAdded || '').split(' ')[0]}</td>
                  <td className="rowact">
                    <button
                      className="sm danger"
                      title={`Permanently delete "${(l.description || '').slice(0, 60)}" from the live database`}
                      onClick={() => deleteRow(l._id)}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
              {!shown.length && !loading && (
                <tr>
                  <td colSpan={12} className="hint" style={{ textAlign: 'center', padding: 24 }}>
                    No locations {filter.trim() || country ? 'match the filter' : `found for "${loadedFor ?? uploader}"`}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {pager}
      </div>

      {(() => {
        const row = lightboxId ? allRows.find((r) => r._id === lightboxId) : null;
        if (!row || !imageCount(row)) return null;
        return (
          <PhotoLightbox
            l={row}
            onClose={() => setLightboxId(null)}
            manage={{
              busy: photoBusy,
              onAdd: (files) => addPhotos(row._id, files),
              onDelete: (key) => deletePhoto(row._id, key),
            }}
            onReorder={(keys) => reorderPhotos(row._id, keys)}
            reorderBusy={photoBusy}
          />
        );
      })()}
    </div>
  );
};

/* ---------- small presentational helpers ---------- */
function escapeHtml(s: any) {
  return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
}
// React already escapes text content, so we just coerce to string for display.
function escapeText(s: any) {
  return String(s);
}

const Thumb: React.FC<{ l: PlaceItem; onClick: () => void }> = ({ l, onClick }) => {
  const u = firstImageUrl(l);
  const [retry, setRetry] = useState(0);
  const [dead, setDead] = useState(false);
  // Reset when the row's image changes (e.g. after a new search).
  useEffect(() => {
    setRetry(0);
    setDead(false);
  }, [u]);
  if (!u) return <span className="hint">—</span>;
  if (dead) return <span className="hint">n/a</span>;
  const n = imageCount(l);
  // Esri's imagery server intermittently 500s when many thumbnails load at once.
  // Retry a few times with backoff (cache-busted) — this also staggers the
  // requests so the burst that triggers the throttling clears itself.
  const src = retry ? u + (u.includes('?') ? '&' : '?') + '_r=' + retry : u;
  return (
    <img
      src={src}
      className="thumb"
      width={48}
      height={34}
      loading="lazy"
      alt=""
      title={`${n} photo(s) — click to preview`}
      onClick={onClick}
      onError={() => {
        if (retry < 4) window.setTimeout(() => setRetry((r) => r + 1), 500 * (retry + 1));
        else setDead(true);
      }}
    />
  );
};

const Chips: React.FC<{ arr: string[] }> = ({ arr }) => {
  if (!arr || !arr.length) return <span className="hint">—</span>;
  return (
    <div className="chips">
      {arr.map((x, i) => (
        <span className="chip" key={i}>
          {x}
        </span>
      ))}
    </div>
  );
};

/* ---------- multi-select list used for services / activities ----------
   Both fields are enums the mobile app filters on, so they are picked from a
   checkbox list rather than typed. `instant` reports every tick straight away
   (the import table edits local rows); without it the picks are buffered until
   Save, which is what the Manage view needs because each save is a live
   database write. An "other value" box is still there for a value the enum
   does not cover yet. */
const CheckboxPicker: React.FC<{
  title: string;
  options: string[];
  values: string[];
  instant?: boolean;
  onChange: (next: string[]) => void;
  onClose: () => void;
}> = ({ title, options, values, instant, onChange, onClose }) => {
  const [draft, setDraft] = useState<string[]>(values || []);
  const [custom, setCustom] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);

  // Clicking away closes the list — but only when every tick has already been
  // applied. Buffered picks (Manage view) would be lost, so there the Cancel /
  // Save buttons are the only way out.
  useEffect(() => {
    if (!instant) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [instant, onClose]);
  const picked = new Set(draft);
  const apply = (next: string[]) => {
    setDraft(next);
    if (instant) onChange(next);
  };
  const toggle = (opt: string) =>
    apply(picked.has(opt) ? draft.filter((v) => v !== opt) : [...draft, opt]);
  const addCustom = () => {
    const v = custom.trim();
    if (v && !picked.has(v)) apply([...draft, v]);
    setCustom('');
  };
  return (
    <div
      ref={boxRef}
      className="pickbox"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div className="pickbox-head">
        <b>{title}</b>
        <span className="count">{draft.length} ticked</span>
        <span className="spacer" />
        {draft.length > 0 && (
          <button type="button" className="linkbtn" onClick={() => apply([])}>
            clear
          </button>
        )}
        {instant ? (
          <button type="button" className="sm" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button type="button" className="sm" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="blue sm"
              onClick={() => {
                onChange(draft);
                onClose();
              }}
            >
              Save
            </button>
          </>
        )}
      </div>
      <div className="pickbox-list">
        {options.map((o) => (
          <label key={o} className={picked.has(o) ? 'on' : ''}>
            <input type="checkbox" checked={picked.has(o)} onChange={() => toggle(o)} />
            <span>{o}</span>
          </label>
        ))}
      </div>
      <div className="pickbox-add">
        <input
          type="text"
          value={custom}
          placeholder="other value…"
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addCustom();
            }
          }}
        />
        <button type="button" className="sm" disabled={!custom.trim()} onClick={addCustom}>
          + add
        </button>
      </div>
    </div>
  );
};

/* ---------- coordinates ----------
   Latitude and longitude are only ever wanted together (pasted into Maps, a
   message, a ticket), so one click takes both. The pair is selected as well as
   copied: the selection is the visible feedback, and it keeps Ctrl+C working
   when the clipboard API is unavailable (an insecure origin, or the browser
   denied permission). */
async function copyText(text: string, selected?: HTMLElement | null): Promise<boolean> {
  if (selected) {
    const range = document.createRange();
    range.selectNodeContents(selected);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Older path — still the only one that works outside a secure context.
    try {
      if (selected) return document.execCommand('copy');
      // Nothing on screen to select (the Manage view's button): copy the text
      // from a throwaway field, so the glyph on the button can't come along.
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

const CoordCell: React.FC<{ lat: number; lon: number }> = ({ lat, lon }) => {
  const [copied, setCopied] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const text = `${lat}, ${lon}`;
  return (
    <td
      className="mono coordcell"
      title={`Click to select and copy "${text}"`}
      onClick={async () => {
        if (await copyText(text, ref.current)) {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1200);
        }
      }}
    >
      <span ref={ref}>{text}</span>
      {copied && <span className="copied">copied ✓</span>}
    </td>
  );
};

// Manage view keeps latitude and longitude in separate editable cells, so the
// pair is copied from a small button instead — clicking the number itself
// still opens the editor.
const CopyCoords: React.FC<{ lat: number; lon: number }> = ({ lat, lon }) => {
  const [copied, setCopied] = useState(false);
  const text = `${lat}, ${lon}`;
  return (
    <button
      type="button"
      className="coordcopy"
      title={`Copy both coordinates ("${text}")`}
      onClick={async (e) => {
        e.stopPropagation(); // not an edit click
        if (await copyText(text)) {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1200);
        }
      }}
    >
      {copied ? '✓' : '⧉'}
    </button>
  );
};

const PayPill: React.FC<{ p: string }> = ({ p }) => {
  const cls = p === 'Free' ? 'free' : p === 'Paid' ? 'paid' : '';
  return <span className={'pill ' + cls}>{p}</span>;
};

interface EditableCellProps {
  l: PlaceItem;
  field: string;
  editing: { id: string; field: string } | null;
  setEditing: (e: { id: string; field: string } | null) => void;
  commit: (id: string, field: string, value: string) => void;
  render: (value: any) => React.ReactNode;
}
// Fields long enough that a single-line inline input makes editing painful
// (you can only see one word at a time) — these get the big modal editor.
const LONG_TEXT_FIELDS = ['description'];

const EditableCell: React.FC<EditableCellProps> = ({ l, field, editing, setEditing, commit, render }) => {
  const isEditing = editing && editing.id === l._id && editing.field === field;
  if (isEditing && LONG_TEXT_FIELDS.includes(field)) {
    return (
      <td className="editcell editcell-wrap">
        {render(l[field])}
        <LongTextEditor
          label={field}
          initial={l[field] ?? ''}
          onSave={(v) => commit(l._id, field, v)}
          onClose={() => setEditing(null)}
        />
      </td>
    );
  }
  if (isEditing) {
    return (
      <td>
        <input
          type="text"
          autoFocus
          defaultValue={l[field] ?? ''}
          style={{ padding: '3px 5px' }}
          onFocus={(e) => e.target.select()}
          onBlur={(e) => commit(l._id, field, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') setEditing(null);
          }}
        />
      </td>
    );
  }
  return (
    <td
      className={'editcell' + (LONG_TEXT_FIELDS.includes(field) ? ' editcell-wrap' : '')}
      onClick={() => setEditing({ id: l._id, field })}
    >
      {render(l[field])}
    </td>
  );
};

// Big modal editor for long text (description) — fixed-position overlay, so
// it isn't clipped by the table's scroll container (same trick as the photo
// lightbox).
const LongTextEditor: React.FC<{
  label: string;
  initial: string;
  onSave: (v: string) => void;
  onClose: () => void;
}> = ({ label, initial, onSave, onClose }) => {
  const [val, setVal] = useState(initial);
  const save = () => onSave(val);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) save();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [val, onClose]); // save() closes over val/onSave; re-binding each keystroke is fine here

  return (
    <div className="longtext-backdrop" onClick={onClose}>
      <div className="longtext-box" onClick={(e) => e.stopPropagation()}>
        <label>{label}</label>
        <textarea autoFocus value={val} onChange={(e) => setVal(e.target.value)} onFocus={(e) => e.target.select()} />
        <div className="longtext-foot">
          <span className="hint">{val.length} characters · Ctrl+Enter to save · Esc to cancel</span>
          <span className="spacer" />
          <button className="sm" onClick={onClose}>
            Cancel
          </button>
          <button className="primary sm" onClick={save}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
